/**
 * Player skins: proportions, palette, and the pixels themselves.
 *
 * Painted in code rather than loaded from PNGs, the same as the block atlas.
 * That keeps the repo free of binary art, makes a character tweakable by
 * changing numbers, and - because this file touches no DOM and no engine -
 * lets the skins be rendered and inspected headlessly.
 *
 * The three are Cossack archetypes drawn from the "Козаки" trio: a broad
 * strongman, a tall lean one under a papakha, and a short stout one.
 *
 * Skins are cosmetic only. Every character has the same collision box.
 */

/** Skin textures are square. Generous, so the packer never has to be clever. */
export const SKIN_TEX = 128;

/**
 * Model height in pixels. Every character sums to this, so all three stand
 * eye to eye and the camera sits at the same height regardless of skin.
 */
export const MODEL_PX_HEIGHT = 32;

export interface BoxSize {
  /** Along X (the character's left-right). */
  w: number;
  /** Along Y. */
  h: number;
  /** Along Z (front-back). */
  d: number;
}

export interface BodyProportions {
  head: BoxSize;
  torso: BoxSize;
  arm: BoxSize;
  leg: BoxSize;
  /** Optional headwear box, sitting on top of the head. */
  hat?: BoxSize;
  /**
   * A second skin, a little proud of the first.
   *
   * What a body is wearing, as its own geometry rather than as paint on the
   * body. Everything up to now has been painted *on*: a leaf dress is the
   * torso coloured green, so it can never hang past the hip, and hair is the
   * head coloured dark, so it can never lie on a shoulder. That ceiling is
   * what a second layer lifts.
   *
   * Given in pixels of swell - how much wider the outer box is than the one
   * under it - because that is the thing being decided. A part left out has
   * no overlay at all, which is most parts on most bodies.
   *
   * Anything the painter does not paint on an overlay face stays fully
   * transparent and is cut away, so a sleeve can end in rags and hair can
   * end in strands.
   */
  overlay?: {
    head?: number; torso?: number; arm?: number; leg?: number;
  };
  /**
   * Read off a skin file rather than packed by us.
   *
   * Set on anything whose texture is a PNG somebody drew. It changes where
   * the faces are, and nothing else: the boxes are the same boxes.
   */
  classic?: boolean;
}

/**
 * Where a skin's picture lives, for the ones that have one.
 *
 * A file under `public/skins/`, drawn in any skin editor and exported at
 * 64x64. Set it and the painter is not called at all: what the body wears is
 * what is in the file, which is the only way a character comes out looking
 * exactly like the drawing of it. Everything else here paints its skin in
 * code, and code approximating a picture is a guess that takes a dozen goes
 * and is still a guess.
 */
export interface SkinImage {
  readonly file: string;
}

/**
 * What a character is wearing.
 *
 * Not a cosmetic detail: it decides how paintSkin dresses the body, because a
 * плахта and a пара шароварів are not the same garment with a different colour.
 * Everything drawn before the villagers arrived is "cossack", so adding this
 * changed nothing that already existed.
 */
export type Attire =
  | "cossack" | "peasant" | "peasantWoman" | "orc"
  /** Вовкулака: a man's build, a wolf's head, and fur instead of clothes. */
  | "wolf"
  /** Мавка: a white shirt, bare feet, and a вінок of green in her hair. */
  | "mavka"
  /** Скелет-страж: bone under a helmet, a red tabard, and iron. */
  | "skeleton"
  /** Чахлик Невмирущий: a crown on a dead man, and a cloak of black feathers. */
  | "chakhlyk";

export interface Palette {
  skin: number;
  skinShade: number;
  /** Sorochka or zhupan. */
  cloth: number;
  clothShade: number;
  /** Embroidery, stripes, trim. */
  trim: number;
  trousers: number;
  trousersShade: number;
  belt: number;
  boot: number;
  /** Hair, oseledets and moustache. */
  hair: number;
  hatMain?: number;
  hatCrown?: number;
  /** Плахта: the wrapped skirt, and the stripe woven through it. */
  skirt?: number;
  skirtStripe?: number;
  /** Намисто - the strings of coral a woman wears at the throat. */
  beads?: number;
  /** Хустка - the headscarf. */
  scarf?: number;
  /** Camouflage: the darker and lighter blotches over the base cloth. */
  camoDark?: number;
  camoLight?: number;
  /** The letter daubed on the chest, and the snout. */
  mark?: number;
  snout?: number;
  /**
   * Light coming off the thing itself, and the softer edge of it.
   *
   * The мавка's marks: forehead, cheeks, the collar of rays, the wrists. Not
   * a trim colour - trim is thread, and this is meant to look like it is lit
   * from behind. There is no emissive channel on this material, so what
   * makes it read as glow is that it is far brighter than anything near it
   * and it has a softer halo around it.
   */
  glow?: number;
  glowSoft?: number;
}

export interface SkinDef {
  id: string;
  /** Ukrainian name, shown in the picker. */
  name: string;
  /** The same name in English. Required, like every other label here. */
  en: string;
  /** One line of flavour, also shown in the picker. */
  blurb: string;
  blurbEn: string;
  proportions: BodyProportions;
  palette: Palette;
  /**
   * A drawn skin, instead of a painted one.
   *
   * With this set the model takes its texture from the file and `paintSkin`
   * is never asked. The proportions must be the classic ones - that is what
   * the file's layout assumes - and `classic` must be set with it.
   */
  image?: SkinImage;
  /** Defaults to "cossack", which is what the three playable skins are. */
  attire?: Attire;
  /**
   * How tall this body is against a grown-up's, 1 being one.
   *
   * Every skin is thirty-two pixels from sole to crown - that is what keeps
   * the camera at one eye level whatever you choose to look like - so
   * proportions can say what shape a body is and cannot say how big it is.
   * A child is the one thing in the game that has to be *smaller*, and this
   * is the one number that says so.
   */
  stature?: number;
}

// --- palettes --------------------------------------------------------------
const FLESH = 0xe8b48c;
const FLESH_SHADE = 0xcf9770;
const BLACK_HAIR = 0x1a1512;
const BOOT = 0x241f1c;
const RED_CLOTH = 0xc4302b;
const RED_DARK = 0xa02420;
const BELT_RED = 0x8e1f1c;
const CREAM = 0xf2ead8;
const CREAM_SHADE = 0xd9cfba;
const WHITE_SHIRT = 0xf8f4ea;
const WHITE_SHADE = 0xdfd8c9;
const EMBROIDERY = 0xb8332e;
const PAPAKHA = 0x6b4a32;
// The villagers, straight off a photograph: unbleached linen, a straw бриль,
// a striped плахта, and coral намисто at the throat.
const LINEN = 0xf1ebdc;
const LINEN_SHADE = 0xd8d0be;
const STRAW = 0xd8b35e;
const STRAW_SHADE = 0xb4913f;
const PLAKHTA = 0x5e3230;
const PLAKHTA_STRIPE = 0xc9a24a;
const CORAL = 0xbe2f30;
const SCARF = 0x8f3330;
const SASH = 0xb8863a;
// The orc: a pig in a helmet, which is how this country has drawn the invader
// since the day he arrived. Sickly grey-pink hide, sour green webbing, and the
// letter he paints on his own machines.
const HIDE = 0xb08a86;
const HIDE_SHADE = 0x8e6c69;
const SNOUT = 0xd0a0a4;
const FATIGUE = 0x4a5236;
const CAMO_DARK = 0x333a26;
const CAMO_LIGHT = 0x6b7448;
const HELMET = 0x515a3c;
const MARK = 0xe4e0d4;

export const SKINS: readonly SkinDef[] = [
  {
    id: "sylach",
    name: "Силач", en: "Strongman",
    blurb: "Широкий у плечах. Сорочка та червоний пояс.",
    blurbEn: "Broad in the shoulders. Shirt and a red sash.",
    proportions: {
      head: { w: 8, h: 8, d: 8 },
      torso: { w: 12, h: 13, d: 6 },
      arm: { w: 5, h: 12, d: 5 },
      leg: { w: 6, h: 11, d: 6 },
    },
    palette: {
      skin: FLESH, skinShade: FLESH_SHADE,
      cloth: CREAM, clothShade: CREAM_SHADE,
      trim: EMBROIDERY,
      trousers: RED_CLOTH, trousersShade: RED_DARK,
      belt: BELT_RED, boot: BOOT, hair: BLACK_HAIR,
    },
  },
  {
    id: "kharakternyk",
    name: "Характерник", en: "Kharakternyk",
    blurb: "Високий і сухий. Червоний жупан, шапка-папаха.",
    blurbEn: "Tall and lean. Red zhupan, papakha hat.",
    proportions: {
      head: { w: 7, h: 8, d: 7 },
      torso: { w: 7, h: 11, d: 4 },
      arm: { w: 3, h: 14, d: 3 },
      leg: { w: 4, h: 13, d: 4 },
      hat: { w: 9, h: 7, d: 9 },
    },
    palette: {
      skin: FLESH, skinShade: FLESH_SHADE,
      cloth: RED_CLOTH, clothShade: RED_DARK,
      trim: 0xded3c4,
      trousers: RED_DARK, trousersShade: 0x8e1f1c,
      belt: BELT_RED, boot: BOOT, hair: BLACK_HAIR,
      hatMain: PAPAKHA, hatCrown: RED_CLOTH,
    },
  },
  {
    id: "dzhura",
    name: "Джура", en: "Dzhura",
    blurb: "Малий та кремезний. Велика голова, довгі вуса.",
    blurbEn: "Short and stocky. Big head, long moustache.",
    proportions: {
      head: { w: 9, h: 9, d: 9 },
      torso: { w: 9, h: 11, d: 5 },
      arm: { w: 4, h: 10, d: 4 },
      leg: { w: 5, h: 12, d: 5 },
    },
    palette: {
      skin: FLESH, skinShade: FLESH_SHADE,
      cloth: WHITE_SHIRT, clothShade: WHITE_SHADE,
      trim: EMBROIDERY,
      trousers: RED_CLOTH, trousersShade: RED_DARK,
      belt: BELT_RED, boot: BOOT, hair: BLACK_HAIR,
    },
  },
];

/**
 * The people who live here.
 *
 * Drawn from a photograph of a village couple: he in a linen sorochka and a
 * straw бриль, she in an embroidered sorochka, a striped плахта and coral
 * намисто. Two of them, and not two of the same, because a village needs a
 * чоловік and a жінка.
 *
 * Kept apart from `SKINS` because the villagers the world puts in a village
 * are these two and only these two, and that list is read for that - but
 * they *are* pickable now, and were the only thing in the game a player
 * could reasonably want to be and could not. See `PICKABLE_SKINS`.
 */
export const VILLAGER_SKINS: readonly SkinDef[] = [
  {
    id: "selianyn",
    name: "Селянин", en: "Villager",
    blurb: "Полотняна сорочка, солом'яний бриль, тканий пояс.",
    blurbEn: "Linen shirt, straw hat, woven belt.",
    attire: "peasant",
    proportions: {
      head: { w: 8, h: 8, d: 8 },
      torso: { w: 10, h: 13, d: 5 },
      arm: { w: 4, h: 12, d: 4 },
      leg: { w: 5, h: 11, d: 5 },
      // Wide and flat: a бриль is all brim and almost no crown.
      hat: { w: 13, h: 3, d: 13 },
    },
    palette: {
      skin: FLESH, skinShade: FLESH_SHADE,
      cloth: LINEN, clothShade: LINEN_SHADE,
      trim: EMBROIDERY,
      // White linen trousers, not red sharovary. This is the whole difference
      // between a villager and a cossack at a glance.
      trousers: LINEN, trousersShade: LINEN_SHADE,
      belt: SASH, boot: BOOT, hair: BLACK_HAIR,
      hatMain: STRAW, hatCrown: STRAW_SHADE,
    },
  },
  {
    id: "selianka",
    name: "Селянка", en: "Villager",
    blurb: "Вишиті рукави, плахта в смужку, коралове намисто.",
    blurbEn: "Embroidered sleeves, striped plakhta, coral beads.",
    attire: "peasantWoman",
    proportions: {
      head: { w: 8, h: 8, d: 8 },
      torso: { w: 8, h: 11, d: 4 },
      arm: { w: 3, h: 12, d: 3 },
      leg: { w: 5, h: 13, d: 5 },
    },
    palette: {
      skin: FLESH, skinShade: FLESH_SHADE,
      cloth: LINEN, clothShade: LINEN_SHADE,
      trim: EMBROIDERY,
      trousers: LINEN, trousersShade: LINEN_SHADE,
      belt: SASH, boot: BOOT, hair: 0x3b2a1e,
      skirt: PLAKHTA, skirtStripe: PLAKHTA_STRIPE,
      beads: CORAL, scarf: SCARF,
    },
  },
];

/**
 * Орк.
 *
 * Kept apart from the villagers for the same reason they are kept apart from
 * the playable three: this is not somebody you can be. The prototype is the
 * caricature this country has drawn of the invader since the day he arrived -
 * a pig in a helmet, in sour green, with the letter he paints on his own
 * machines daubed across his chest.
 *
 * No flag on the helmet. At sixteen pixels to a body it would be three
 * unreadable dots; the snout, the helmet and the letter carry it.
 */
export const ORC_SKIN: SkinDef = {
  id: "orc",
  name: "Орк", en: "Orc",
    blurb: "Свиняча морда, каска, буква на грудях.",
    blurbEn: "Pig's face, helmet, a letter on its chest.",
  attire: "orc",
  proportions: {
    // Heavier than a man and a little shorter in the leg: it should read as
    // something squat coming at you rather than as a tall silhouette.
    head: { w: 9, h: 9, d: 9 },
    torso: { w: 11, h: 12, d: 7 },
    arm: { w: 5, h: 12, d: 5 },
    leg: { w: 5, h: 11, d: 5 },
    hat: { w: 10, h: 4, d: 10 },
  },
  palette: {
    skin: HIDE, skinShade: HIDE_SHADE,
    cloth: FATIGUE, clothShade: CAMO_DARK,
    trim: CAMO_LIGHT,
    trousers: FATIGUE, trousersShade: CAMO_DARK,
    belt: 0x2b2a22, boot: 0x1e1c18, hair: 0x3a3228,
    hatMain: HELMET, hatCrown: CAMO_DARK,
    camoDark: CAMO_DARK, camoLight: CAMO_LIGHT,
    mark: MARK, snout: SNOUT,
  },
};

/**
 * Орк-лучник: the same beast, kitted for shooting.
 *
 * Lighter in the shoulder than the one with the sabre and without the helmet,
 * so at a distance you can tell which is coming for you and which is standing
 * off - which matters, because the answer to each is the opposite of the
 * answer to the other.
 */
export const ORC_ARCHER_SKIN: SkinDef = {
  ...ORC_SKIN,
  id: "orc_archer",
  name: "Орк-лучник", en: "Orc archer",
  blurb: "Сухіший за побратима, без каски. Тримає лук.",
  blurbEn: "Leaner than its fellow, no helmet. Carries a bow.",
  proportions: {
    ...ORC_SKIN.proportions,
    torso: { w: 9, h: 12, d: 6 },
    arm: { w: 4, h: 13, d: 4 },
    hat: { w: 9, h: 2, d: 9 },
  },
  palette: {
    ...ORC_SKIN.palette,
    // No helmet: a rag round the head instead, so the silhouette is flatter.
    hatMain: 0x6b6552, hatCrown: 0x4d4839,
  },
};

/**
 * Орк-ватажок, from the photograph.
 *
 * Pale and hairless where the others are green and helmeted, big-headed, and
 * built long in the arm - the thing in the picture is hunched and reaching,
 * and a squat orc silhouette would not read as it at all. The necklace is the
 * detail everything else hangs off, so it gets the trim colour and the belt
 * is dropped to keep the eye on it.
 *
 * It is not drawn bigger than the rest. Height is how this game says "that
 * one is dangerous" nowhere else, and one enemy that breaks the scale of the
 * world would look like a bug before it looked like a boss - it is told apart
 * by colour, by proportion and by the speed it comes at you.
 */
export const ORC_LEADER_SKIN: SkinDef = {
  ...ORC_SKIN,
  id: "orc_leader",
  name: "Орк-ватажок", en: "Orc leader",
  blurb: "Блідий, лисий, довгорукий. Разок на шиї.",
  blurbEn: "Pale, bald, long-armed. A necklace at its throat.",
  proportions: {
    head: { w: 10, h: 10, d: 10 },
    torso: { w: 11, h: 11, d: 7 },
    // The long reach of the thing in the photograph.
    arm: { w: 5, h: 15, d: 5 },
    leg: { w: 6, h: 11, d: 6 },
    /*
     * Bald, but not sizeless.
     *
     * A hat of zero height leaves its top face with no pixels behind it, and
     * the atlas check caught that: an unpainted face is a hole waiting for
     * whatever happens to be next to it in the texture. So it keeps a hat one
     * pixel deep, in the colour of its own scalp.
     */
    hat: { w: 9, h: 1, d: 9 },
  },
  palette: {
    ...ORC_SKIN.palette,
    skin: 0xd8cfc2, skinShade: 0xa89c8c,
    cloth: 0xd8cfc2, clothShade: 0xa89c8c,
    trousers: 0xc9bfb0, trousersShade: 0x9a8f80,
    // The necklace, and nothing competing with it.
    trim: 0x2f2b26, belt: 0x2f2b26,
    boot: 0xa89c8c, hair: 0xd8cfc2,
    hatMain: 0xd8cfc2, hatCrown: 0xd8cfc2,
    snout: 0xc0b3a4, mark: 0x2f2b26,
  },
};

/**
 * Вовкулака.
 *
 * The Ukrainian werewolf is a man turned, not a wolf grown large - so this is
 * a man's proportions with a wolf's head on them and fur where the clothes
 * would be. Long in the arm and slightly stooped in the torso, because what
 * it does is run at you and reach.
 *
 * Dark grey-brown, which is what a вовк is here, and amber eyes, which is the
 * only thing on it you will see first in a wood at night. Nothing on it is
 * green and nothing on it is a uniform: it must not be mistaken for an орк
 * across a field, and colour is the only thing that says so at that range.
 */
export const VOVKULAKA_SKIN: SkinDef = {
  id: "vovkulaka",
  name: "Вовкулака", en: "Werewolf",
  blurb: "Вовча голова на людських плечах. Бурштинові очі й кігті.",
  blurbEn: "A wolf's head on a man's shoulders. Amber eyes and claws.",
  attire: "wolf",
  proportions: {
    head: { w: 8, h: 9, d: 9 },
    torso: { w: 10, h: 12, d: 6 },
    arm: { w: 5, h: 14, d: 5 },
    leg: { w: 5, h: 11, d: 5 },
    // The ears. Small, and the only thing standing off the skull.
    hat: { w: 8, h: 3, d: 4 },
  },
  /*
   * Charcoal, off the sheet, and it used to be brown.
   *
   * Six tones and five of them are nearly the same black - the whole animal
   * is one colour and reads by its shape and its mottling. The sixth is the
   * bone of the claws and the teeth, and it is the only light thing on him
   * apart from his eyes: those two and nothing else is what you see of a
   * вовкулака in a wood at night, which is the point of him.
   */
  palette: {
    skin: 0x2e2e33, skinShade: 0x1d1d21,
    cloth: 0x2e2e33, clothShade: 0x26262b,
    trim: 0x3c3c43,
    trousers: 0x2e2e33, trousersShade: 0x1d1d21,
    belt: 0x1d1d21, boot: 0x15151a, hair: 0x26262b,
    hatMain: 0x22222a, hatCrown: 0x1a1a20,
    snout: 0x3a3a41,
    // The claws and the teeth: old bone, not white.
    glow: 0xd6d8c2, glowSoft: 0xa8aa96,
  },
};

/**
 * Мавка.
 *
 * Not a monster to look at, which is the whole of what makes her one - and
 * she used to be a young woman in a white shirt with a вінок, barefoot in a
 * lake at night. That is one мавка out of the stories and it was the safe
 * one to draw: a drowned girl reads at a glance and asks nothing of anybody.
 *
 * This is the other one, off the prototype: not a girl who went into the
 * wood but the wood itself, wearing a girl. A willow for hair, a fall of
 * leaves for a dress, skin the pale grey-green of the underside of a leaf -
 * and light coming off her, at the brow, under the eyes, in a collar of rays
 * at the throat and in a band at each wrist. The light is the only warm
 * colour on her and everything else is green, which is what makes you look
 * at it.
 *
 * The colours are read off the picture rather than invented: the leaves are
 * three greens, dark where they overlap and yellow-green where the sun gets
 * in; the cord at her waist is the only thing on her a person made.
 */
export const MAVKA_SKIN: SkinDef = {
  id: "mavka",
  name: "Мавка", en: "Mavka",
  blurb: "Верба замість волосся, листя замість сорочки, і світиться. Не варто йти за нею.",
  blurbEn: "Willow for hair, leaves for a shirt, and she glows. Do not follow her.",
  attire: "mavka",
  /*
   * Drawn, not painted.
   *
   * She is the first character in this game to come out of a file instead of
   * out of a painter, and she is the reason the file path exists at all:
   * four goes at painting her in code produced a soldier's jacket, a cactus,
   * a mosaic and a girl in a blue mask, each one a different distance from
   * the drawing rather than nearer to it. The drawing is a picture; the way
   * to look like a picture is to be one.
   *
   * `attire` stays "mavka" because it is not only about paint - it is what
   * decides she comes out at the water's edge at night and goes at dawn.
   */
  image: { file: "mavka_128x128.png" },
  proportions: {
    // The classic measurements, which this game's own rule already agreed
    // with: eight, twelve and twelve is thirty-two either way.
    classic: true,
    head: { w: 8, h: 8, d: 8 },
    torso: { w: 8, h: 12, d: 4 },
    arm: { w: 4, h: 12, d: 4 },
    leg: { w: 4, h: 12, d: 4 },
    /*
     * No second layer, for now.
     *
     * Pavlo, looking at her in the game: "щось зайве на мавці" - and it was.
     * The second-layer rectangles of her file are a scatter of dark green,
     * about a third of each one, and a second layer is worn a pixel proud of
     * the part under it on every side. A pixel out from her head, that
     * scatter is not texture: it is debris. It hangs off her silhouette as
     * thin vertical slivers down both sides of her face - the side faces of
     * the box, seen nearly edge-on, with only the few surviving pixels of
     * each column to show - and the top face puts a dark bar across the sky
     * above her head. Rendered without it, her face is exactly the face in
     * the drawing.
     *
     * Halving the swell did not help: the slivers are thinner and the face
     * is still under a third of a layer of dark green.
     *
     * It comes back with the hair and the cape, which are step two and are
     * their own thing to undo - with rectangles drawn to be worn out there,
     * rather than noise that happened to land in them.
     */
  },
  /*
   * Kept, though nothing draws her with them any more.
   *
   * The palette is what the game asks when it wants *her colours* for
   * something that is not her body: the glow on a nameplate, a particle, the
   * light she throws. Deleting it would trade a texture for a handful of
   * small holes elsewhere.
   */
  palette: {
    skin: 0xa9c184, skinShade: 0x86a065,
    cloth: 0x4a7331, clothShade: 0x27431e,
    trim: 0x8a6a3c, belt: 0x5c4326,
    trousers: 0x4a7331, trousersShade: 0x355d2a,
    boot: 0xa9c184, hair: 0x2c5324,
    hatMain: 0x3d6529, hatCrown: 0x6f9a3f,
    glow: 0xf8efa0, glowSoft: 0xd8d472,
  },
};

/**
 * Who the player may be.
 *
 * The three козаки and the two селяни. Not the orcs, not the вовкулака and
 * not the мавка: what those are for is being met, and a player wearing one
 * would be a player nobody else in the world could read. There is nothing
 * clever keeping them out - they are simply not on this list, and the picker
 * shows this list.
 *
 * The селяни were let in on 2026-09-12 because they were the one thing in
 * the game somebody could plainly want to be and could not. A skin is a
 * look and nothing else - a селянка carries the same sabre as a козак.
 */
export const PICKABLE_SKINS: readonly SkinDef[] = [...SKINS, ...VILLAGER_SKINS];

/** Everything that can be worn by anything, playable or not. */
/**
 * Скелет-страж, off the reference sheet.
 *
 * Not the white stick-skeleton every other game draws: a domed helmet with a
 * nose-guard, a red tabard hanging over the ribs, and iron. Two metres and a
 * bit tall and narrow with it - the drawing gives 2.1 by 0.6, and the
 * proportions here are that shape rather than that number, because a body in
 * this game is built out of five boxes and the numbers are pixels of skin.
 *
 * Bone is not white. Old bone is the colour of weak tea, and white bone on a
 * dark wall reads as paper.
 */
export const SKELETON_SKIN: SkinDef = {
  id: "skeleton",
  name: "Скелет-страж", en: "Skeleton guard",
  blurb: "Шолом із наносником, накидка, меч і щит. Стоїть, доки не підійдеш.",
  blurbEn: "A nasal helm, a tabard, a sword and a shield. It stands until you come near.",
  attire: "skeleton",
  proportions: {
    // Narrow and tall: a skeleton has no flesh on it.
    /*
     * Eight, twelve and twelve: every body in this game is thirty-two tall.
     *
     * Not a style rule - the atlas is packed on it and a check says so. The
     * reference calls the guard 2.1 blocks against a person's 1.8, and the
     * way to get that here is the *proportions within* the thirty-two: a
     * narrow chest and long limbs read as tall where four extra pixels would
     * only have overflowed the sheet.
     */
    head: { w: 8, h: 8, d: 8 },
    torso: { w: 8, h: 12, d: 4 },
    arm: { w: 3, h: 13, d: 3 },
    leg: { w: 4, h: 12, d: 4 },
    /*
     * The helmet, which is the thing you see first.
     *
     * Six tall, not four. A box nine across and four high sitting on a head
     * is a slab - it read as a builder's helmet, and the drawing's is a dome
     * that comes to a point. Height is what makes the difference; the rest
     * is paint, darkening the corners so the rows step in as they rise.
     */
    hat: { w: 9, h: 6, d: 9 },
  },
  palette: {
    // Old bone, not paper.
    skin: 0xd8d2bc, skinShade: 0xa9a289,
    // The tabard, and the dark leather under it.
    cloth: 0x8e2f2a, clothShade: 0x5e1f1c,
    trim: 0xc8a44a,
    trousers: 0x3a342c, trousersShade: 0x282420,
    belt: 0x4a3b2a, boot: 0x2a2620, hair: 0x8e8878,
    hatMain: 0x9aa0a8, hatCrown: 0xc2c8d0,
  },
};

/**
 * Чахлик Невмирущий, off the drawing.
 *
 * A dead man in a crown: grey-green skin stretched over nothing, a ribcage
 * showing through, a cloak of black feathers off the shoulders, and one eye
 * far too big for the socket it is in. The crown is gold with black spikes
 * and it is the only bright thing on him.
 *
 * Tall and starved. The silhouette is the whole character - long thin limbs,
 * a narrow chest, and a crown - so the proportions lean further from a
 * person's than anything else in this game.
 */
export const CHAKHLYK_SKIN: SkinDef = {
  id: "chakhlyk",
  name: "Чахлик Невмирущий", en: "Chakhlyk the Undying",
  blurb: "Корона на мерці, плащ із чорного пір'я і синій вогонь у руці.",
  blurbEn: "A crown on a dead man, a cloak of black feathers, and cold blue fire.",
  attire: "chakhlyk",
  proportions: {
    // Thirty-two all told, like every other body here. What makes him look
    // starved is where the thirty-two goes: a short chest over long legs.
    head: { w: 8, h: 8, d: 8 },
    torso: { w: 8, h: 11, d: 4 },
    arm: { w: 3, h: 15, d: 3 },
    leg: { w: 4, h: 13, d: 4 },
    // The crown, and it is the biggest thing he wears.
    hat: { w: 10, h: 5, d: 10 },
  },
  palette: {
    // Grey-green, and paler on the bone that shows through.
    skin: 0xa8b29c, skinShade: 0x7d8874,
    // The feather cloak.
    cloth: 0x23232a, clothShade: 0x141418,
    trim: 0x8e8a78,
    trousers: 0x1c1c22, trousersShade: 0x121216,
    belt: 0x6b3f2a, boot: 0x121216, hair: 0x23232a,
    // Gold band, black spikes.
    hatMain: 0xc8a44a, hatCrown: 0x1a1a20,
    glow: 0x9fd8ff, glowSoft: 0x4f93c8,
  },
};

/**
 * The children, who are drawn and are not chosen.
 *
 * Their own list rather than two more entries in `VILLAGER_SKINS`, because
 * that list is what the picker offers: "there are five of them - three козаки
 * and two селяни" is a check, and a player choosing to be a six-year-old
 * would be looking through a camera at grown-up eye height out of the top of
 * a child's head. They live in villages; they are not somebody you can be.
 */
export const CHILD_SKINS: readonly SkinDef[] = [
  /*
   * And the children.
   *
   * "Створи дітей і дівчат і хлопців." The same two people, smaller: a child
   * in this game is the village pair with a head that has not caught up with
   * the body yet, which is how children read at sixteen pixels. No бриль on
   * the boy - a straw hat is a working man's, and he is not one yet.
   *
   * Beside the grown-ups rather than in `SKINS`, for the same reason they
   * are: a village is made of these four and that list is read for that.
   */
  {
    id: "khlopchyk",
    name: "Хлопчик", en: "Village boy",
    blurb: "Полотняна сорочка, підперезана мотузкою. Босий.",
    blurbEn: "A linen shirt belted with cord. Barefoot.",
    attire: "peasant",
    stature: 0.72,
    proportions: {
      // A big head on a short body, which is the whole of what makes a shape
      // read as a child - and `stature` is what makes it small.
      head: { w: 8, h: 9, d: 8 },
      torso: { w: 8, h: 11, d: 4 },
      arm: { w: 3, h: 10, d: 3 },
      leg: { w: 4, h: 12, d: 4 },
    },
    palette: {
      skin: FLESH, skinShade: FLESH_SHADE,
      cloth: LINEN, clothShade: LINEN_SHADE,
      trim: EMBROIDERY,
      trousers: LINEN, trousersShade: LINEN_SHADE,
      belt: SASH, boot: FLESH, hair: 0x6b4a2a,
    },
  },
  {
    id: "divchynka",
    name: "Дівчинка", en: "Village girl",
    blurb: "Вишита сорочка та плахта, як у матері, тільки менша.",
    blurbEn: "An embroidered shirt and plakhta, like her mother's but small.",
    attire: "peasantWoman",
    stature: 0.7,
    proportions: {
      head: { w: 8, h: 9, d: 8 },
      torso: { w: 7, h: 11, d: 4 },
      arm: { w: 3, h: 10, d: 3 },
      leg: { w: 4, h: 12, d: 4 },
    },
    palette: {
      skin: FLESH, skinShade: FLESH_SHADE,
      cloth: LINEN, clothShade: LINEN_SHADE,
      trim: EMBROIDERY,
      trousers: LINEN, trousersShade: LINEN_SHADE,
      belt: SASH, boot: FLESH, hair: 0x8a6a3a,
      skirt: PLAKHTA, skirtStripe: PLAKHTA_STRIPE,
      beads: CORAL, scarf: SCARF,
    },
  },
];

/**
 * The мавка after dark: the same drawing, since the drawing is only what
 * a model cannot yet do, and her own model - see skinModels - red-eyed and
 * thorned. Not pickable: she is for being met.
 */
export const MAVKA_NIGHT_SKIN: SkinDef = {
  ...MAVKA_SKIN,
  id: "mavkaNight",
  name: "Нічна мавка", en: "Night mavka",
  blurb: "Та сама, після заходу сонця. Очі червоні. Не підходь до води.",
  blurbEn: "The same one, after sunset. Red eyes. Keep away from the water.",
};

export const ALL_SKINS: readonly SkinDef[] = [
  ...SKINS, ...VILLAGER_SKINS, ...CHILD_SKINS,
  ORC_SKIN, ORC_ARCHER_SKIN, ORC_LEADER_SKIN,
  VOVKULAKA_SKIN, MAVKA_SKIN, MAVKA_NIGHT_SKIN, SKELETON_SKIN, CHAKHLYK_SKIN,
];

export function skinById(id: string): SkinDef {
  return ALL_SKINS.find((s) => s.id === id) ?? SKINS[0]!;
}

// --- texture layout --------------------------------------------------------

export interface Rect { x: number; y: number; w: number; h: number; }

/** The six face rectangles of one unwrapped box. */
export interface BoxFaces {
  top: Rect; bottom: Rect;
  right: Rect; left: Rect;
  front: Rect; back: Rect;
  /** Total footprint of the unwrap, for packing. */
  bounds: Rect;
}

/**
 * Standard box unwrap: depth-width-depth across, depth then height down.
 *
 *        [ d ][ w ][ d ]
 *   d          top  bottom
 *   h    right front left  back
 */
export function unwrapBox(x: number, y: number, s: BoxSize): BoxFaces {
  const { w, h, d } = s;
  return {
    top:    { x: x + d,             y, w, h: d },
    bottom: { x: x + d + w,         y, w, h: d },
    right:  { x,                    y: y + d, w: d, h },
    front:  { x: x + d,             y: y + d, w, h },
    left:   { x: x + d + w,         y: y + d, w: d, h },
    back:   { x: x + d + w + d,     y: y + d, w, h },
    bounds: { x, y, w: 2 * d + 2 * w, h: d + h },
  };
}

export type PartName = "head" | "hat" | "torso" | "armR" | "armL" | "legR" | "legL"
  | "headOver" | "torsoOver" | "armROver" | "armLOver" | "legROver" | "legLOver";

/** The outer box for a part, or undefined where there is none. */
export function overlayOf(
  p: BodyProportions, part: "head" | "torso" | "arm" | "leg",
): BoxSize | undefined {
  const swell = p.overlay?.[part];
  if (swell === undefined) return undefined;
  const base = part === "head" ? p.head
    : part === "torso" ? p.torso
    : part === "arm" ? p.arm : p.leg;
  // Swollen on every side, so it sits evenly proud of what it covers.
  return { w: base.w + swell * 2, h: base.h + swell, d: base.d + swell * 2 };
}

export type SkinLayout = Partial<Record<PartName, BoxFaces>>;

/**
 * Pack every part into the texture with a simple shelf packer: place left to
 * right, wrap to a new row when the next part will not fit.
 *
 * Deterministic for a given set of proportions, and the painter and the UV
 * mapper both call this, so they can never disagree about where a face is.
 */
/**
 * The classic 64x64 skin layout, doubled.
 *
 * This is the one everybody's skin editor writes and the one the drawing
 * Pavlo brought was rendered from - head at the origin, body at 16,16, the
 * limbs round them, and the second layer on the same sheet below and beside.
 * There is no arguing with it: it is where the pixels are in the file.
 *
 * Given here at twice size because this game's atlas is 128 and `faceUV`
 * divides by that. A 64-pixel skin drawn into a 128 sheet at two-to-one
 * lands exactly on these rectangles, every existing piece of UV arithmetic
 * keeps working, and nothing anywhere has to learn about a second texture
 * size. Nearest-neighbour doubling of pixel art is lossless.
 */
function classicLayout(p: BodyProportions): SkinLayout {
  const at = (x: number, y: number, size: BoxSize): BoxFaces =>
    unwrapClassic(x * 2, y * 2, size);
  const layout: SkinLayout = {
    head: at(0, 0, p.head),
    torso: at(16, 16, p.torso),
    armR: at(40, 16, p.arm),
    armL: at(32, 48, p.arm),
    legR: at(0, 16, p.leg),
    legL: at(16, 48, p.leg),
  };
  /*
   * The second layer's rectangles are the same size as the first's.
   *
   * This is how a skin file is: the overlay boxes get identical rectangles
   * and the *model* draws them a little larger, stretching the same pixels
   * over a slightly bigger box. Reserving swollen rectangles instead - which
   * is what our own packer does, because it packs what it is given - ran the
   * left sleeve off the right-hand edge of the sheet, and `layoutFits` said
   * so. The swell lives in the geometry and nowhere else.
   */
  if (p.overlay?.head !== undefined) layout.headOver = at(32, 0, p.head);
  if (p.overlay?.torso !== undefined) layout.torsoOver = at(16, 32, p.torso);
  if (p.overlay?.arm !== undefined) {
    layout.armROver = at(40, 32, p.arm);
    layout.armLOver = at(48, 48, p.arm);
  }
  if (p.overlay?.leg !== undefined) {
    layout.legROver = at(0, 32, p.leg);
    layout.legLOver = at(0, 48, p.leg);
  }
  return layout;
}

/**
 * One box unwrapped the way a skin file has it.
 *
 * Not the same order as this game's own `unwrapBox` - a skin puts the two
 * caps side by side along the top and the four walls in a row underneath,
 * left, front, right, back. Writing it out rather than deriving it, because
 * the format is a fact about other people's files and not a rule with a
 * reason inside it.
 */
function unwrapClassic(x: number, y: number, s: BoxSize): BoxFaces {
  const w = s.w * 2, h = s.h * 2, d = s.d * 2;
  return {
    top: { x: x + d, y, w, h: d },
    bottom: { x: x + d + w, y, w, h: d },
    right: { x, y: y + d, w: d, h },
    front: { x: x + d, y: y + d, w, h },
    left: { x: x + d + w, y: y + d, w: d, h },
    back: { x: x + d + w + d, y: y + d, w, h },
    bounds: { x, y, w: d + w + d + w, h: d + h },
  };
}

export function layoutSkin(p: BodyProportions): SkinLayout {
  // A skin that comes out of a file is laid out the way that file is.
  if (p.classic === true) return classicLayout(p);
  const parts: [PartName, BoxSize][] = [
    ["head", p.head],
    ["torso", p.torso],
    ["armR", p.arm],
    ["armL", p.arm],
    ["legR", p.leg],
    ["legL", p.leg],
  ];
  if (p.hat !== undefined) parts.push(["hat", p.hat]);
  // The outer layer, packed like any other box: it is geometry with its own
  // faces, and the atlas does not care what it is for.
  const over = overlayOf(p, "head");
  if (over !== undefined) parts.push(["headOver", over]);
  const overTorso = overlayOf(p, "torso");
  if (overTorso !== undefined) parts.push(["torsoOver", overTorso]);
  const overArm = overlayOf(p, "arm");
  if (overArm !== undefined) {
    parts.push(["armROver", overArm], ["armLOver", overArm]);
  }
  const overLeg = overlayOf(p, "leg");
  if (overLeg !== undefined) {
    parts.push(["legROver", overLeg], ["legLOver", overLeg]);
  }

  const layout: SkinLayout = {};
  let x = 0;
  let y = 0;
  let rowHeight = 0;

  for (const [name, size] of parts) {
    const width = 2 * size.d + 2 * size.w;
    const height = size.d + size.h;
    if (x + width > SKIN_TEX) {
      x = 0;
      y += rowHeight;
      rowHeight = 0;
    }
    layout[name] = unwrapBox(x, y, size);
    x += width;
    rowHeight = Math.max(rowHeight, height);
  }
  return layout;
}

/** Does this layout fit inside the texture? Guards against fat proportions. */
export function layoutFits(layout: SkinLayout): boolean {
  for (const faces of Object.values(layout)) {
    const b = faces.bounds;
    if (b.x + b.w > SKIN_TEX || b.y + b.h > SKIN_TEX) return false;
  }
  return true;
}

/**
 * UV rectangle for one face rectangle of the skin texture.
 *
 * V counts down from the top because textures upload with flipY. This is the
 * same convention as the block atlas, and getting it backwards there rendered
 * the entire world black without raising anything - so it lives in one tested
 * function rather than being written out at each call site.
 */
export function faceUV(r: Rect): {
  u0: number; u1: number; vTop: number; vBottom: number;
} {
  return {
    u0: r.x / SKIN_TEX,
    u1: (r.x + r.w) / SKIN_TEX,
    vTop: 1 - r.y / SKIN_TEX,
    vBottom: 1 - (r.y + r.h) / SKIN_TEX,
  };
}

// --- painting --------------------------------------------------------------

function put(px: Uint8ClampedArray, x: number, y: number, color: number): void {
  if (x < 0 || y < 0 || x >= SKIN_TEX || y >= SKIN_TEX) return;
  const i = (y * SKIN_TEX + x) * 4;
  px[i] = (color >> 16) & 0xff;
  px[i + 1] = (color >> 8) & 0xff;
  px[i + 2] = color & 0xff;
  px[i + 3] = 255;
}

/**
 * Take a pixel back out, leaving a hole.
 *
 * Only meaningful on the second layer, where the material throws away
 * anything fully transparent. On the body itself a hole is a window into the
 * inside of a person.
 */
function clear(px: Uint8ClampedArray, x: number, y: number): void {
  if (x < 0 || y < 0 || x >= SKIN_TEX || y >= SKIN_TEX) return;
  px[(y * SKIN_TEX + x) * 4 + 3] = 0;
}

function fill(px: Uint8ClampedArray, r: Rect, color: number): void {
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) put(px, r.x + x, r.y + y, color);
  }
}

/** Fill a horizontal band inside a rect, given fractional start and end. */
function band(
  px: Uint8ClampedArray, r: Rect, from: number, to: number, color: number,
): void {
  const y0 = Math.round(r.h * from);
  const y1 = Math.round(r.h * to);
  fill(px, { x: r.x, y: r.y + y0, w: r.w, h: Math.max(1, y1 - y0) }, color);
}

/**
 * Eyes, nose and the moustache that makes a cossack a cossack.
 *
 * Rows are chosen explicitly rather than by fraction: at eight pixels tall
 * there is room for exactly one row of moustache plus a drooping tip, and a
 * row of chin below it. Spend more than that and it reads as a beard.
 */
function paintFace(
  px: Uint8ClampedArray, r: Rect, pal: Palette, moustache = true,
  /**
   * What the whites of the eyes are made of.
   *
   * White, for everybody with blood in them. The мавка's are the same light
   * as the rest of her marks - white eyes in a green face read as a frog,
   * and hers are meant to be the thing you notice from across the water.
   */
  eye = 0xffffff,
): void {
  const { x, y, w, h } = r;

  // Big round eyes - the cartoon cossacks have them, and at this size a
  // subtle eye is no eye at all.
  const eyeY = y + Math.round(h * 0.28);
  const inset = Math.max(1, Math.round(w * 0.14));
  const leftX = x + inset;
  const rightX = x + w - inset - 2;
  fill(px, { x: leftX, y: eyeY, w: 2, h: 2 }, eye);
  fill(px, { x: rightX, y: eyeY, w: 2, h: 2 }, eye);
  // Pupils sit toward the middle, which reads as looking ahead.
  put(px, leftX + 1, eyeY + 1, 0x201a16);
  put(px, rightX, eyeY + 1, 0x201a16);

  const noseY = eyeY + 2;
  fill(px, { x: x + Math.floor(w / 2) - 1, y: noseY, w: 2, h: 1 }, pal.skinShade);

  const mY = Math.min(y + h - 3, noseY + 1);
  if (!moustache) {
    // A mouth instead: two pixels, so the face is not simply blank below the
    // nose. Any wider and it reads as a grimace at this size.
    fill(px, { x: x + Math.floor(w / 2) - 1, y: mY + 1, w: 2, h: 1 }, 0x9c5a4c);
    return;
  }
  // Moustache: one full row, tips dropping one row at the outer edge only,
  // and always at least one row of chin left underneath.
  fill(px, { x, y: mY, w, h: 1 }, pal.hair);
  put(px, x, mY + 1, pal.hair);
  put(px, x + w - 1, mY + 1, pal.hair);
}

/**
 * The oseledets: a shaved head with one long lock.
 *
 * The head must stay bald - a wide patch of hair reads as an ordinary
 * haircut and loses the whole silhouette. Two pixels, off-centre.
 */
function paintOseledets(px: Uint8ClampedArray, head: BoxFaces, pal: Palette): void {
  const t = head.top;
  const strip = 2;
  const sx = t.x + Math.floor((t.w - strip) / 2);
  // A narrow lock running back across an otherwise bare crown.
  fill(px, { x: sx, y: t.y, w: strip, h: t.h }, pal.hair);
  // It falls forward over one side of the forehead, not straight down.
  const f = head.front;
  const fx = f.x + Math.max(1, Math.floor(f.w * 0.28));
  fill(px, { x: fx, y: f.y, w: strip, h: 1 }, pal.hair);
  put(px, fx, f.y + 1, pal.hair);
}

/** Vertical stripes, for the плахта. Woven cloth, so they run with the warp. */
function stripes(
  px: Uint8ClampedArray, r: Rect, base: number, stripe: number,
): void {
  fill(px, r, base);
  for (let x = 0; x < r.w; x++) {
    if (x % 2 !== 0) continue;
    fill(px, { x: r.x + x, y: r.y, w: 1, h: r.h }, stripe);
  }
}

/**
 * A headscarf, tied under the chin.
 *
 * Covers the crown and the back and sides of the head, but stops short of the
 * face - the fringe of hair between the scarf and the eyes is what stops it
 * reading as a helmet.
 */
function paintScarf(px: Uint8ClampedArray, head: BoxFaces, pal: Palette): void {
  const scarf = pal.scarf ?? SCARF;
  fill(px, head.top, scarf);
  fill(px, head.back, scarf);
  for (const face of [head.left, head.right]) {
    band(px, face, 0, 0.55, scarf);
  }
  // A lighter band round the crown, so it reads as tied cloth.
  band(px, head.back, 0.10, 0.18, pal.beads ?? CORAL);
  for (const face of [head.left, head.right]) band(px, face, 0.10, 0.18, pal.beads ?? CORAL);
  // A band across the forehead, with hair showing beneath it.
  band(px, head.front, 0, 0.14, scarf);
  band(px, head.front, 0.14, 0.22, pal.hair);
}

/** A small deterministic hash, 0..1. The world has its own; this is local. */
function hashPixel(x: number, y: number): number {
  let h = x * 374761393 + y * 668265263;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return (h >>> 0) / 4294967296;
}

/**
 * Blotches of camouflage over whatever is already there.
 *
 * Deterministic from each pixel's place in the texture, so a garment comes out
 * the same every time and the two sleeves are not obvious copies. The sample
 * is smeared across pairs of pixels, so neighbours agree more often than they
 * disagree - which is the difference between blotches and static.
 */
function camo(
  px: Uint8ClampedArray, r: Rect, dark: number, light: number,
): void {
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) {
      const n = hashPixel(r.x + x, r.y + y);
      const m = (n + hashPixel(r.x + ((x / 2) | 0), r.y + ((y / 2) | 0))) / 2;
      if (m > 0.72) put(px, r.x + x, r.y + y, light);
      else if (m < 0.30) put(px, r.x + x, r.y + y, dark);
    }
  }
}

/**
 * A snout, and the small eyes above it.
 *
 * The whole character is here: a helmet on a man is a soldier, and a helmet on
 * a pig is the reference.
 */
function paintSnout(px: Uint8ClampedArray, r: Rect, pal: Palette): void {
  const { x, y, w, h } = r;
  const snout = pal.snout ?? 0xd0a0a4;

  // Small, close together and low - a pig's eyes, not a cossack's saucers.
  const eyeY = y + Math.round(h * 0.30);
  fill(px, { x: x + 2, y: eyeY, w: 2, h: 1 }, 0x241c18);
  fill(px, { x: x + w - 4, y: eyeY, w: 2, h: 1 }, 0x241c18);

  const snoutY = y + Math.round(h * 0.52);
  const snoutH = Math.max(2, Math.round(h * 0.28));
  fill(px, { x: x + 2, y: snoutY, w: w - 4, h: snoutH }, snout);
  // The underside in shadow is what gives it depth at this size.
  fill(px, { x: x + 2, y: snoutY + snoutH - 1, w: w - 4, h: 1 }, pal.skinShade);
  const nostril = snoutY + Math.max(0, Math.floor(snoutH / 2) - 1);
  put(px, x + Math.floor(w / 2) - 2, nostril, 0x3a2622);
  put(px, x + Math.floor(w / 2) + 1, nostril, 0x3a2622);
}

/**
 * A wolf's head: a long muzzle down the middle and eyes that catch the light.
 *
 * Not the pig's snout with a different colour. A pig's snout is a flat disc
 * on the front of the face and a wolf's is a wedge that comes *out* of it, so
 * the muzzle here is a narrow column down the centre with the jaw dark under
 * it - which is the only way to say "this is long" on a flat square.
 *
 * The eyes are the other half of it: small, amber, and set wide. Dark eyes on
 * a dark head are no eyes at all, and the thing has to be visible coming at
 * you through trees.
 */
function paintMuzzle(px: Uint8ClampedArray, r: Rect, pal: Palette): void {
  const { x, y, w, h } = r;
  const cx = x + Math.floor(w / 2);

  /*
   * The eyes: amber, and the brightest thing on him by a long way.
   *
   * Everything else is within thirty points of black, so these do all the
   * work of saying where he is looking - and in a wood at night they are
   * most of what there is to see. Set under a dark brow, which is what makes
   * them look at you rather than sit on the front of a head.
   */
  const eyeY = y + Math.round(h * 0.28);
  const amber = 0xe8a838;
  const glint = 0xf6d27a;
  fill(px, { x: x + 1, y: eyeY - 1, w: w - 2, h: 1 }, pal.skinShade);
  for (const ex of [x + 1, x + w - 3]) {
    fill(px, { x: ex, y: eyeY, w: 2, h: 2 }, amber);
    put(px, ex, eyeY, glint);
    // The slit, which is what stops two amber squares reading as goggles.
    put(px, ex + 1, eyeY + 1, 0x14100c);
  }

  // The wedge: wide at the eyes, narrowing to the nose.
  const muzzleTop = eyeY + 2;
  const snout = pal.snout ?? 0x3a3a41;
  for (let i = 0; muzzleTop + i < y + h - 2; i++) {
    const half = i < 2 ? 2 : 1;
    fill(px, { x: cx - half, y: muzzleTop + i, w: half * 2, h: 1 }, snout);
  }
  // A black nose on the end of it.
  fill(px, { x: cx - 1, y: y + h - 4, w: 2, h: 1 }, 0x0f0c0a);

  /*
   * And the snarl, which the old head did not have at all.
   *
   * It ended in a jaw of plain shadow - a wolf's head with its mouth shut,
   * which is a dog. The sheet has him showing his teeth in every view. Two
   * rows with the dark of the mouth between them: uppers hanging down,
   * lowers standing up, and the fangs at the corners longer than the rest.
   */
  const mouth = y + h - 3;
  fill(px, { x: cx - 2, y: mouth, w: 5, h: 3 }, 0x0d0b09);
  // The softer bone, not the claw white: a full-brightness grin four pixels
  // wide on an eight-pixel head is a skull, and he has a muzzle over it.
  const bone = pal.glowSoft ?? 0xa8aa96;
  for (let i = 0; i < 5; i++) {
    const tx = cx - 2 + i;
    if (i % 2 === 0) put(px, tx, mouth, bone);
    else put(px, tx, mouth + 1, bone);
  }
  // The two fangs at the corners, and they are the only bright ones.
  const fang = pal.glow ?? 0xd6d8c2;
  fill(px, { x: cx - 2, y: mouth, w: 1, h: 2 }, fang);
  fill(px, { x: cx + 2, y: mouth, w: 1, h: 2 }, fang);
}

/**
 * Fur: dark on dark, in patches rather than in lines.
 *
 * The opposite problem to the мавка's willow, and it wants the opposite
 * answer. Hers hangs, so it is painted in strands running down. A wolf's
 * coat lies along the body in *clumps* - light catches the top of a patch
 * and the hollow beside it stays black - so what this does is blot: a few
 * tones a shade apart, in blobs two or three pixels across, with no
 * direction to them at all.
 *
 * It has to be quiet. The sheet's six greys sit within about thirty points
 * of each other, and that is deliberate: at any contrast worth noticing this
 * stops being fur and becomes camouflage, which is the mistake the мавка
 * spent two goes climbing out of.
 */
function paintFur(
  px: Uint8ClampedArray, r: Rect, dark: number, mid: number, light: number,
): void {
  for (let y = 0; y < r.h; y++) {
    for (let x = 0; x < r.w; x++) {
      /*
       * The blob, not the pixel, and only a little break-up over it.
       *
       * Neighbours share a cell so the tone comes out in clumps. The first
       * version then re-rolled a quarter of the pixels individually, which
       * is not fur but static - at a glance the whole animal fizzed. Six per
       * cent is enough to stop the clumps reading as tiles and little enough
       * that the coat still lies in patches.
       */
      const cell = hashPixel(
        r.x + Math.floor(x / 3) * 3, r.y + Math.floor(y / 2) * 2);
      const grain = hashPixel(r.x + x + 37, r.y + y + 11);
      let c = cell < 0.34 ? dark : cell > 0.74 ? light : mid;
      if (grain > 0.97) c = light;
      else if (grain < 0.03) c = dark;
      put(px, r.x + x, r.y + y, c);
    }
  }
}

/**
 * The claws at the end of a limb, which is half of what he looks like.
 *
 * Four of them on the sheet, pale bone against all that black, and they are
 * the second thing you see after the eyes. Drawn at the very foot of the
 * face with a dark line above, because a claw grows out of a toe and not out
 * of the fur: without that line it reads as a sock.
 */
function paintClaws(
  px: Uint8ClampedArray, r: Rect, bone: number, shade: number, dark: number,
): void {
  /*
   * One row, three claws, and only where toes actually are.
   *
   * The first version put two rows of them across the whole width of every
   * face of the limb - and since a limb has four faces, what came round the
   * paw was an unbroken band of pale blocks. From the front it read as a row
   * of piano keys, and the four of them together looked more like teeth than
   * the teeth did. Claws are small, there are three you can see, and they
   * are on the front of the foot.
   */
  const foot = r.y + r.h - 1;
  fill(px, { x: r.x, y: foot - 1, w: r.w, h: 1 }, dark);
  /*
   * Every other pixel, so the toes have gaps between them.
   *
   * Spacing them evenly across the width put the three of them on pixels
   * one, two and three of a five-wide paw - touching, which is a pale bar
   * and not three claws. What makes something read as separate at this size
   * is the dark between, and there is only room for that on alternate
   * columns.
   */
  for (let x = 0; x < r.w; x += 2) {
    put(px, r.x + x, foot, bone);
    put(px, r.x + x, foot - 1, shade);
  }
}

/**
 * Вінок: the wreath a мавка wears, and the only colour on her.
 *
 * Green all the way round with flowers set into it at intervals - red and
 * white, which is what a вінок is made of and which are the two colours that
 * hold up at four pixels. Without it she is a pale woman in a white shirt,
 * and half the point of a мавка is that you know what she is on sight.
 */
/**
 * Верба: leaves hanging in strands, which is what she is made of.
 *
 * Not a texture of scattered leaves. A willow reads as *strands* - long thin
 * leaves overlapping downward - so this works a column at a time, and each
 * column is a hanging chain of them: a bright pixel where a leaf catches the
 * light, two of its body, a dark one where the next leaf overlaps its tip.
 * The chains are offset against each other by a hash of the column, or the
 * tips line up into stripes and it reads as corduroy.
 */
function leafFall(
  px: Uint8ClampedArray, r: Rect,
  dark: number, mid: number, light: number,
): void {
  /*
   * Leaves, one at a time, down each column.
   *
   * Four goes at this, and the two failures either side of it are the map.
   *
   * Give every column its own tone out of a hash and you get camouflage -
   * three greens shuffled across nine pixels is an army jacket. Give the
   * strands a fixed width with the shadow always on the third column and you
   * get a *rib*, repeated exactly, which at the size she is actually met at
   * reads as a cactus. Widen the strands to break that up and each one
   * becomes a block of flat colour four across and eight down: a mosaic.
   *
   * What the drawing has is neither. It has *leaves*: narrow, three to six
   * long, overlapping down the column with the next one starting wherever
   * the last finished, each a shade from its neighbour and none of them far
   * from the others. So that is what this draws - a leaf at a time, dark
   * where it ends, a lighter pixel where the light catches its shoulder, and
   * the column beside it starting at a different height.
   */
  for (let x = 0; x < r.w; x++) {
    let y = 0;
    // Where this column's first leaf is cut off by the top of the panel, so
    // the leaves interleave instead of lining up in rows.
    let n = Math.floor(hashPixel(r.x + x, r.y + 7) * 5);
    while (y < r.h) {
      /*
       * Four to eight long, not three to six.
       *
       * A leaf of three is body, highlight, tip - and a run of two, which
       * measured against random speckle's one and a quarter is no distance
       * at all. Both the eye and the check want the same thing here: a leaf
       * has to last long enough to be a leaf. The drawing's are long.
       */
      const span = 4 + Math.floor(hashPixel(r.x + x, r.y + n * 3 + 11) * 5);
      const tint = hashPixel(r.x + x, r.y + n * 5 + 23);
      const body = tint > 0.74 ? light : mid;
      for (let i = 0; i < span && y < r.h; i++, y++) {
        // The shoulder of the leaf catches the light; the tip is where it
        // overlaps the one below and goes dark.
        const c = i === 0 ? body
          : i === span - 1 ? dark
          : i === 1 && tint > 0.86 ? light
          : body;
        put(px, r.x + x, r.y + y, c);
      }
      n++;
    }
  }
}

/**
 * The lights in her leaves.
 *
 * Scattered, few, and never in a row - the drawing has a dozen of them over
 * the whole figure and they are the reason she reads as something that is
 * lit from the inside rather than as a woman wearing a plant. Placed from
 * the pixel's own position so they land in the same places every time this
 * is painted, which it is not more than once, but the atlas is cached and a
 * skin that flickered between rebuilds would be a bug nobody could catch.
 */
function leafGlow(
  px: Uint8ClampedArray, r: Rect, glow: number, soft: number,
): void {
  for (let y = 1; y < r.h - 1; y++) {
    for (let x = 0; x < r.w; x++) {
      const n = hashPixel(r.x + x + 101, r.y + y + 57);
      if (n > 0.965) {
        put(px, r.x + x, r.y + y, glow);
        // A little of it on the leaf below, so the speck sits in the foliage
        // rather than on top of it like a sequin.
        if (y + 1 < r.h) put(px, r.x + x, r.y + y + 1, soft);
      }
    }
  }
}

/**
 * The collar of rays, which is the first thing you see on her.
 *
 * A ring of light at the throat with spikes hanging off it, long and short
 * alternating. Drawn after the leaves and the cord so it lies over both -
 * light falls on top of what it is lit by.
 */
function paintRayCollar(
  px: Uint8ClampedArray, r: Rect, glow: number, soft: number,
): void {
  const top = r.y + Math.max(1, Math.round(r.h * 0.06));
  fill(px, { x: r.x, y: top, w: r.w, h: 1 }, glow);
  /*
   * The teeth, with the dress showing between them.
   *
   * Drawn first as long spikes and short ones, in the two glow tones - and
   * at nine pixels across, two tones that near each other are one tone: it
   * came out a solid yellow bib. A ray reads because of what is *not* lit
   * beside it, so every other column is left alone and the spikes get their
   * length from the light tone alone.
   */
  for (let x = 0; x < r.w; x++) {
    if (x % 2 !== 0) continue;
    put(px, r.x + x, top + 1, glow);
    // The longest rays are the outer pair and the middle one, as on the
    // picture - a collar of teeth all the same length is a saw.
    if (x === 0 || x === r.w - 1 || x === (r.w - 1) / 2) {
      put(px, r.x + x, top + 2, soft);
    }
  }
}

/**
 * The mark on her forehead, and the light under her eyes.
 *
 * Eight pixels of face, and the eyes take the row a forehead would want, so
 * the mark gets exactly one row plus a dot in the hairline above it. That is
 * enough: it is the brightest thing on a green face.
 */
function paintMavkaFace(
  px: Uint8ClampedArray, r: Rect, glow: number, soft: number,
): void {
  const mid = r.x + Math.floor(r.w / 2);
  // The dot above, sitting in the hair, and the leaf on the brow below it.
  put(px, mid, r.y, glow);
  fill(px, { x: mid - 1, y: r.y + 1, w: 3, h: 1 }, soft);
  put(px, mid, r.y + 1, glow);
  // And the light on her cheeks, under the outer corner of each eye. One
  // pixel each: two ran into the eye above and the pair became one blob.
  const cheek = r.y + Math.round(r.h * 0.28) + 2;
  put(px, r.x, cheek, glow);
  put(px, r.x + r.w - 1, cheek, glow);
  put(px, r.x, cheek + 1, soft);
  put(px, r.x + r.w - 1, cheek + 1, soft);
}

/**
 * A skull: two holes and a row of teeth.
 *
 * Sockets rather than eyes. An eye painted white with a dark pupil is a face
 * looking at you; a socket is a hole with nothing in it, and the difference
 * between the two is the whole of what makes a skeleton a skeleton. So the
 * sockets are the *darkest* thing on the head and there is nothing in them.
 */
function paintSkull(
  px: Uint8ClampedArray, r: Rect, pal: Palette,
): void {
  const { x, y, w, h } = r;
  const socket = 0x0d0c09;
  const eyeY = y + Math.round(h * 0.34);
  /*
   * Sockets big enough to be holes.
   *
   * Two by two read as eyes with something behind them - a face. A skull has
   * nothing behind the sockets, and at eight pixels the way to say so is to
   * spend three of them across and two down and let the shadow under the
   * brow run into it. This is the single strongest signal that the thing
   * coming at you is dead rather than dressed up.
   */
  fill(px, { x: x + 1, y: eyeY, w: 3, h: 2 }, socket);
  fill(px, { x: x + w - 4, y: eyeY, w: 3, h: 2 }, socket);
  // The brow above them, which is what makes the sockets look sunk.
  fill(px, { x: x + 1, y: eyeY - 1, w: w - 2, h: 1 }, pal.skinShade);
  // The nose: a hole, narrowing downwards.
  const mid = x + Math.floor(w / 2);
  put(px, mid - 1, eyeY + 2, socket);
  put(px, mid, eyeY + 2, socket);
  put(px, mid, eyeY + 3, socket);
  // The jaw, hanging: a dark line under the teeth is the gap of an open
  // mouth, and without it the teeth read as a row of stitching.
  const jaw = Math.min(y + h - 2, eyeY + 4);
  for (let i = 1; i < w - 1; i++) {
    put(px, x + i, jaw, i % 2 === 0 ? socket : 0xe8e2cc);
  }
  fill(px, { x: x + 1, y: jaw + 1, w: w - 2, h: 1 }, socket);
  // And the helm's nasal, straight down the middle of the face.
  fill(px, { x: mid - 1, y: y, w: 2, h: eyeY - y + 2 },
    pal.hatMain ?? 0x9aa0a8);
  put(px, mid - 1, eyeY + 2, 0x6e747c);
  put(px, mid, eyeY + 2, 0x6e747c);
}

/**
 * A limb that is a bone rather than a sleeve.
 *
 * The one trick that makes a box-model skeleton work. Every limb here is a
 * solid three or four pixels across, and painting it bone-coloured gives a
 * bone three pixels thick - which is an arm in a white shirt. So the *edges*
 * are painted the colour of the gap behind the body: what is left lit is a
 * narrow shaft down the middle, and the eye reads the dark as air.
 *
 * The knobs are the other half of it. A shaft with a wider lump at each end
 * is a bone; a shaft without them is a stick.
 */
function paintBone(
  px: Uint8ClampedArray, r: Rect, bone: number, shade: number,
): void {
  const gap = 0x100f0c;
  fill(px, r, gap);
  // The shaft: everything but one pixel of shadow either side, and on a
  // four-wide face one more, so the bone is always narrower than the box.
  const edge = r.w >= 4 ? 1 : 0;
  const shaft = { x: r.x + edge, y: r.y, w: Math.max(1, r.w - edge * 2), h: r.h };
  /*
   * Keep two pixels of bone where there are only two to keep.
   *
   * On a three-wide arm this leaves one lit column and two of gap, which is
   * right. On a four-wide leg the edges take two columns, the shaft is two,
   * and taking another for shade left a *single* lit pixel - so the legs
   * came out near-black with a hairline down them, which in the game read as
   * black trousers rather than as bones. A limb has to have more bone in it
   * than that or the gap is the limb.
   */
  const thin = shaft.w > 2 ? shaft.w - 1 : shaft.w;
  fill(px, { x: shaft.x, y: r.y + 2, w: thin, h: r.h - 4 }, bone);
  fill(px, { x: shaft.x + thin - 1, y: r.y + 2, w: 1, h: r.h - 4 }, shade);
  // The joints, wider than the shaft, top and bottom.
  fill(px, { x: shaft.x, y: r.y, w: shaft.w, h: 2 }, bone);
  fill(px, { x: shaft.x, y: r.y + r.h - 2, w: shaft.w, h: 2 }, bone);
  fill(px, { x: shaft.x + shaft.w - 1, y: r.y, w: 1, h: 2 }, shade);
  fill(px, { x: shaft.x + shaft.w - 1, y: r.y + r.h - 2, w: 1, h: 2 }, shade);
}

/**
 * The ribcage: the front of a skeleton, and the thing the drawing leads with.
 *
 * Dark first, because what is between ribs is the inside of a dead man. Then
 * the ribs themselves - short bars either side of a spine, stepping in as
 * they go down so the cage tapers the way a chest does. Painted over the
 * whole torso, then the tabard is hung over the top of it, which leaves the
 * cage showing where cloth would not cover: at the collar and below the hem.
 */
function paintRibs(
  px: Uint8ClampedArray, r: Rect, bone: number, shade: number,
): void {
  const dark = 0x0f0e0b;
  fill(px, r, dark);
  const mid = r.x + Math.floor(r.w / 2);
  // The spine, and the collarbones over the top of it.
  fill(px, { x: mid - 1, y: r.y + 1, w: 2, h: r.h - 2 }, shade);
  fill(px, { x: r.x + 1, y: r.y, w: r.w - 2, h: 1 }, bone);
  for (let i = 0; i < 4; i++) {
    const ribY = r.y + 2 + i * 2;
    if (ribY >= r.y + r.h - 1) break;
    const inset = 1 + Math.floor(i / 2);
    fill(px, { x: r.x + inset, y: ribY, w: mid - 1 - (r.x + inset), h: 1 },
      bone);
    fill(px, { x: mid + 1, y: ribY, w: r.w - inset - (mid + 1 - r.x), h: 1 },
      bone);
  }
}

/**
 * Чахлик's face: one eye far too big, and the other barely there.
 *
 * Straight off the drawing, and it is the thing that makes him him. The big
 * one is a pale ring with a dark middle - a stare - and the small one is two
 * pixels. Symmetry would make him a monster off a shelf; the asymmetry is
 * what makes him look *wrong*.
 */
function paintDeadKing(
  px: Uint8ClampedArray, r: Rect, pal: Palette,
): void {
  const { x, y, w, h } = r;
  const socket = 0x141018;
  const white = 0xe8e4d8;
  const iris = 0x8e2320;
  const eyeY = y + Math.round(h * 0.28);
  // The big eye: three across, with a red middle.
  fill(px, { x: x + 1, y: eyeY, w: 3, h: 3 }, white);
  put(px, x + 2, eyeY + 1, iris);
  put(px, x + 1, eyeY, socket);
  put(px, x + 3, eyeY + 2, socket);
  // And the other one, which has nearly closed.
  fill(px, { x: x + w - 3, y: eyeY + 1, w: 2, h: 1 }, socket);
  // A mouth of bad teeth, off to one side the way the drawing has it.
  const jaw = Math.min(y + h - 2, eyeY + 4);
  for (let i = 2; i < w - 3; i++) {
    put(px, x + i, jaw, i % 2 === 0 ? socket : pal.skinShade);
  }
}

/**
 * The letter, daubed across the chest.
 *
 * Drawn stroke by stroke rather than from a font: at eleven pixels across
 * there is no font, and the whole point of it is that it reads instantly.
 */
function paintMark(px: Uint8ClampedArray, r: Rect, colour: number): void {
  const w = Math.min(7, r.w - 4);
  const h = Math.min(7, Math.round(r.h * 0.5));
  const x0 = r.x + Math.floor((r.w - w) / 2);
  const y0 = r.y + Math.round(r.h * 0.3);

  fill(px, { x: x0, y: y0, w, h: 1 }, colour);
  fill(px, { x: x0, y: y0 + h - 1, w, h: 1 }, colour);
  for (let i = 0; i < h; i++) {
    const t = (h - 1 - i) / (h - 1);
    put(px, x0 + Math.round(t * (w - 1)), y0 + i, colour);
  }
}

function paintHat(
  px: Uint8ClampedArray, hat: BoxFaces, pal: Palette, size: BoxSize,
): void {
  const main = pal.hatMain ?? PAPAKHA;
  for (const face of [hat.front, hat.back, hat.left, hat.right, hat.bottom]) {
    fill(px, face, main);
  }

  // A бриль is all brim and almost no crown, which is the opposite of a
  // папаха: wide and shallow rather than tall. Painting one like the other
  // gives a flat gold plate floating over the head.
  if (size.h * 3 <= size.w) {
    const t = hat.top;
    const inset = Math.max(1, Math.round(t.w * 0.28));
    fill(px, { x: t.x, y: t.y, w: t.w, h: t.h }, main);
    fill(px, {
      x: t.x + inset, y: t.y + inset,
      w: t.w - 2 * inset, h: t.h - 2 * inset,
    }, pal.hatCrown ?? STRAW_SHADE);
    // The underside of a straw brim is always in shadow.
    fill(px, hat.bottom, pal.hatCrown ?? STRAW_SHADE);
    return;
  }

  // Fur has a lighter upper band; the crown is a coloured cloth top.
  for (const face of [hat.front, hat.back, hat.left, hat.right]) {
    band(px, face, 0, 0.25, pal.hatCrown ?? RED_CLOTH);
  }
  fill(px, hat.top, pal.hatCrown ?? RED_CLOTH);
}

/**
 * Paint one skin into an RGBA buffer.
 *
 * Returns SKIN_TEX * SKIN_TEX * 4 bytes, row-major from the top-left, which is
 * what both a canvas ImageData and a PNG encoder want.
 */
export function paintSkin(def: SkinDef): Uint8ClampedArray {
  const px = new Uint8ClampedArray(SKIN_TEX * SKIN_TEX * 4);
  const layout = layoutSkin(def.proportions);
  const pal = def.palette;
  const attire: Attire = def.attire ?? "cossack";

  // --- head ---------------------------------------------------------------
  const head = layout.head!;
  for (const face of [head.front, head.back, head.left, head.right, head.top, head.bottom]) {
    fill(px, face, pal.skin);
  }
  fill(px, head.back, pal.skinShade);
  if (attire === "orc") {
    paintSnout(px, head.front, pal);
  } else if (attire === "wolf") {
    // Fur first, over the whole head, and the face painted onto it: a flat
    // fill left the cheeks and the back of the skull as plain blocks while
    // the body was mottled, and the join showed.
    for (const face of [head.front, head.back, head.left, head.right, head.top]) {
      paintFur(px, face, pal.skinShade, pal.skin, pal.trim);
    }
    paintMuzzle(px, head.front, pal);
  } else if (attire === "skeleton") {
    paintSkull(px, head.front, pal);
    /*
     * And the rest of the skull, which was a blank cream box.
     *
     * Three faces out of four are what you see of a guard walking away or
     * standing side-on in a corridor, and they were flat - the skull was a
     * picture painted on the front of a head. A hollow at the temple, the
     * dark line where the jaw hangs, and the back of the cranium a shade
     * down: cheap, and it is the difference between a skull and a mask.
     */
    for (const face of [head.left, head.right]) {
      fill(px, face, pal.skin);
      // The temple hollow, behind the socket.
      fill(px, { x: face.x + 1, y: face.y + Math.round(face.h * 0.34), w: 2, h: 2 },
        pal.skinShade);
      // The jaw, and the dark under it.
      fill(px, { x: face.x, y: face.y + face.h - 2, w: face.w, h: 1 }, pal.skinShade);
      fill(px, { x: face.x + 1, y: face.y + face.h - 1, w: face.w - 2, h: 1 },
        0x0d0c09);
    }
    fill(px, head.back, pal.skinShade);
    // The seam over the back of the cranium.
    fill(px, {
      x: head.back.x + Math.floor(head.back.w / 2), y: head.back.y,
      w: 1, h: Math.round(head.back.h * 0.6),
    }, pal.skin);
    fill(px, { x: head.back.x + 1, y: head.back.y + head.back.h - 1,
      w: head.back.w - 2, h: 1 }, 0x0d0c09);
    /*
     * And the helm comes down over the cranium.
     *
     * The hat is its own box sitting on top of the head, so the helmet was a
     * bucket balanced on a cream cube - the whole skull was below it and the
     * iron touched nothing. Painting the top courses of the head itself in
     * iron carries the helm down over the brow, which is where a nasal helm
     * actually sits: cap over the skull, face showing under the rim.
     */
    const iron = pal.hatMain ?? 0x9aa0a8;
    const rim = 0x4a4f56;
    for (const face of [head.front, head.back, head.left, head.right]) {
      fill(px, { x: face.x, y: face.y, w: face.w, h: 2 }, iron);
      fill(px, { x: face.x, y: face.y + 2, w: face.w, h: 1 }, rim);
    }
    fill(px, head.top, iron);
  } else if (attire === "mavka") {
    /*
     * A face, with hair round it - not leaves with a hole in them.
     *
     * The last version painted the whole head in foliage and cut a small
     * window for the face. Against the drawing that is backwards. She is a
     * *girl* - the drawing leads with her face, and the leaves are her hair,
     * behind and around it. What the window gave instead was a pale patch
     * the size of a postage stamp, and a pale patch takes the world's tint:
     * at night the whole world goes blue and she stood in a wood with a blue
     * mask on.
     *
     * So the front of the head is her face nearly all the way across, with
     * a fringe of leaves over the brow and a strand down each side. The
     * sides, the back and the crown are hair.
     */
    for (const face of [head.back, head.left, head.right, head.top]) {
      leafFall(px, face, pal.clothShade, pal.cloth, pal.hatCrown ?? pal.cloth);
      leafGlow(px, face, pal.glow ?? 0xf6e98a, pal.glowSoft ?? 0xcfd47a);
    }
    fill(px, head.front, pal.skin);
    {
      const f = head.front;
      // The fringe: two courses of leaves over the brow, ragged underneath.
      leafFall(px, { ...f, h: 2 }, pal.clothShade, pal.cloth,
        pal.hatCrown ?? pal.cloth);
      for (let x = 0; x < f.w; x++) {
        if (hashPixel(f.x + x, f.y + 61) > 0.55) {
          put(px, f.x + x, f.y + 2, pal.clothShade);
        }
      }
      // And a strand of it down each side of the face.
      for (const ex of [f.x, f.x + f.w - 1]) {
        leafFall(px, { x: ex, y: f.y, w: 1, h: f.h }, pal.clothShade,
          pal.cloth, pal.hatCrown ?? pal.cloth);
      }
    }
    /*
     * Her face, which is the thing the drawing is about.
     *
     * Six pixels of face and every one of them spent: brows, eyes with a
     * dark lash over them, a nose of one pixel, a mouth of one. The iris is
     * in the outer half of each eye so she looks slightly aside - a мавка
     * watching you is worse than a мавка facing you.
     */
    {
      const f = head.front;
      const eyeY = f.y + Math.round(f.h * 0.46);
      const dark = 0x223019;
      for (const ex of [f.x + 1, f.x + f.w - 3]) {
        fill(px, { x: ex, y: eyeY - 1, w: 2, h: 1 }, dark);
        fill(px, { x: ex, y: eyeY, w: 2, h: 2 }, 0xf2f6e6);
        fill(px, { x: ex + 1, y: eyeY, w: 1, h: 2 }, 0x3f6b32);
        put(px, ex + 1, eyeY + 1, dark);
      }
      const mid = f.x + Math.floor(f.w / 2);
      put(px, mid, eyeY + 2, pal.skinShade);
      fill(px, { x: mid - 1, y: eyeY + 4, w: 2, h: 1 }, 0x7a4a44);
      // The mark on her brow, which is the one warm thing on her.
      put(px, mid, f.y + 3, pal.glow ?? 0xf6e98a);
      put(px, mid - 1, f.y + 4, pal.glowSoft ?? 0xcfd47a);
      put(px, mid + 1, f.y + 4, pal.glowSoft ?? 0xcfd47a);
    }
  } else if (attire === "chakhlyk") {
    paintDeadKing(px, head.front, pal);
  } else {
    // A moustache is a cossack's signature and a woman's disfigurement.
    /*
     * A moustache for everybody but the women, which now includes her.
     *
     * The мавка was getting one: the test was `attire !== "peasantWoman"`,
     * and it drew a full-width bar of hair colour across her jaw. On a pale
     * face it passed for a mouth; on a green one with dark green hair it is
     * a moustache, and it is the first thing you see.
     */
    // The мавка has a face of her own now, so this branch never sees her.
    /*
     * A child has no moustache, and that is what `stature` is doing here.
     *
     * The village boy wears the peasant's attire - a linen sorochka is a
     * linen sorochka at any age - and the peasant's face came with a cossack
     * moustache on it. A boy with a moustache is the funniest thing this
     * game has ever drawn by accident and it had to go.
     *
     * Read off how tall the body is rather than off a flag of its own: the
     * one thing that is true of every child ever added here is that they are
     * shorter than a grown-up, and a flag is a thing to forget to set.
     */
    const bearded = attire !== "peasantWoman" && (def.stature ?? 1) >= 1;
    /*
     * Her eyes are pale, and paler than her marks.
     *
     * Painting them in the mark colour put two blocks of bright yellow two
     * pixels either side of two more on the cheeks, and the four of them ran
     * together into what read as blonde hair on a green face. An eye is 2x2
     * here - at that size it has to be quieter than the thing it is next to,
     * or the brightest part of her is her eyeballs.
     */
    paintFace(px, head.front, pal, bearded, 0xffffff);
  }

  if (attire === "peasantWoman") {
    paintScarf(px, head, pal);
  } else if (attire === "mavka") {
    /*
     * Her hair is a willow, and it is most of her.
     *
     * It was loose dark hair with a вінок of flowers on it - a drowned girl,
     * which is one мавка and not this one. From the prototype she is a tree
     * that has decided to be a person: the head is wrapped in hanging willow
     * on every side and the crown box above it is more of the same, lighter
     * where the light gets into it.
     */
    const dark = pal.hair;
    const mid = pal.hatMain ?? dark;
    const light = pal.hatCrown ?? mid;
    leafFall(px, head.top, dark, mid, light);
    leafFall(px, head.back, dark, mid, light);
    leafFall(px, { ...head.front, h: Math.max(1, Math.round(head.front.h * 0.16)) },
      dark, mid, light);
    leafFall(px, { ...head.left, h: Math.round(head.left.h * 0.62) }, dark, mid, light);
    leafFall(px, { ...head.right, h: Math.round(head.right.h * 0.62) }, dark, mid, light);
    if (def.proportions.hat !== undefined) {
      const crown = layout.hat!;
      for (const face of [
        crown.front, crown.back, crown.left, crown.right, crown.bottom,
      ]) {
        leafFall(px, face, dark, mid, light);
      }
      // Lighter on top, where the sun would be on a willow.
      leafFall(px, crown.top, mid, light, pal.glowSoft ?? light);
    }
    // And the light on her, over the hair: a mark lies on the brow, not
    // under the fringe.
    paintMavkaFace(px, head.front, pal.glow ?? 0xf6e98a, pal.glowSoft ?? 0xcfd47a);
  } else if (attire === "wolf") {
    // Fur over the skull, and the hat box cut into two ears.
    if (def.proportions.hat !== undefined) {
      const ears = layout.hat!;
      /*
       * Two ears, not a cap.
       *
       * The box was filled dark on every side, on the theory that dark
       * enough reads as ears. It does not: it reads as a flat bar across the
       * top of his head, which is a cap. What makes two ears out of one box
       * is the *gap* - so the middle third is painted the near-black of the
       * night behind him and the outer thirds are fur, and from any distance
       * the bar breaks into a pair.
       *
       * The silhouette is still one box and nothing can change that. But a
       * silhouette is what you see against the sky, and this animal is only
       * ever met in a wood in the dark, where what you see is the tones.
       */
      const gap = 0x111116;
      const fur = pal.hatMain ?? pal.hair;
      const inner = pal.snout ?? fur;
      for (const face of [ears.front, ears.back, ears.top, ears.bottom]) {
        fill(px, face, fur);
        const third = Math.max(1, Math.round(face.w / 3));
        fill(px, { x: face.x + third, y: face.y, w: face.w - third * 2, h: face.h },
          gap);
      }
      // The sides of the box are the outsides of the ears: all fur.
      fill(px, ears.left, fur);
      fill(px, ears.right, fur);
      // And a hint of the inner ear on the front, which is the only place a
      // wolf's ear is a different colour from the rest of it.
      const third = Math.max(1, Math.round(ears.front.w / 3));
      for (const ex of [ears.front.x, ears.front.x + ears.front.w - third]) {
        fill(px, { x: ex, y: ears.front.y + 1, w: Math.max(1, third - 1),
          h: Math.max(1, ears.front.h - 1) }, inner);
      }
    }
    fill(px, head.top, pal.hair);
  } else if (attire === "skeleton" && def.proportions.hat !== undefined) {
    /*
     * A domed helm with a nose-guard, off the reference.
     *
     * Iron over the whole crown and a dark band at the brow where it sits on
     * the skull, so it reads as *worn* rather than as the head being taller.
     * The nose-guard is one pixel down the middle of the face - small, and
     * the thing that says Kyivan Rus rather than fantasy.
     */
    const helm = layout.hat!;
    for (const face of [helm.front, helm.back, helm.left, helm.right, helm.top]) {
      fill(px, face, pal.hatMain ?? 0x9aa0a8);
    }
    /*
     * Rounded by paint: the corners of the top rows are cut away.
     *
     * A helm is a dome and this is a box, so the two upper corners of each
     * side are painted the colour of what is behind them. The silhouette
     * stays square - nothing can change that - but the shading steps in as
     * it rises, and from any distance it reads as a cone.
     */
    const sky = 0x2a2f36;
    for (const face of [helm.front, helm.back, helm.left, helm.right]) {
      for (let row = 0; row < 3; row++) {
        const cut = 3 - row;
        for (let i = 0; i < cut; i++) {
          put(px, face.x + i, face.y + row, sky);
          put(px, face.x + face.w - 1 - i, face.y + row, sky);
        }
      }
    }
    // The peak catches the light; the rim is in shadow under it.
    band(px, helm.front, 0, 0.34, pal.hatCrown ?? 0xc2c8d0);
    band(px, helm.back, 0, 0.34, pal.hatCrown ?? 0xc2c8d0);
    band(px, helm.left, 0, 0.34, pal.hatCrown ?? 0xc2c8d0);
    band(px, helm.right, 0, 0.34, pal.hatCrown ?? 0xc2c8d0);
    for (const face of [helm.front, helm.back, helm.left, helm.right]) {
      band(px, face, 0.8, 1, 0x4a4f56);
    }
    fill(px, helm.bottom, 0x4a4f56);
    // And the nose-guard, running down the face from under the rim.
    const nose = head.front;
    fill(px, {
      x: nose.x + Math.floor(nose.w / 2) - 1, y: nose.y,
      w: 2, h: Math.round(nose.h * 0.45),
    }, pal.hatMain ?? 0x9aa0a8);
  } else if (attire === "chakhlyk" && def.proportions.hat !== undefined) {
    /*
     * The crown: a gold band with black spikes standing off it.
     *
     * The spikes are the silhouette - five of them, uneven, because the
     * drawing's are uneven and a row of matching teeth is a birthday hat.
     * The band is the only warm colour anywhere on him.
     */
    const crown = layout.hat!;
    for (const face of [crown.front, crown.back, crown.left, crown.right]) {
      fill(px, face, pal.hatMain ?? 0xc8a44a);
      // The spikes rise out of the top two thirds; the band is the bottom.
      for (let x = 0; x < face.w; x++) {
        const tall = [3, 1, 4, 2, 3][x % 5] ?? 2;
        for (let y = 0; y < Math.min(tall, face.h - 1); y++) {
          if ((x + y) % 2 === 0 || y < tall - 1) {
            put(px, face.x + x, face.y + y, pal.hatCrown ?? 0x1a1a20);
          }
        }
      }
      // Studs along the band, which is what the drawing has.
      for (let x = 1; x < face.w; x += 3) {
        put(px, face.x + x, face.y + face.h - 1, 0xefe0a8);
      }
    }
    fill(px, crown.top, pal.hatCrown ?? 0x1a1a20);
    fill(px, crown.bottom, pal.hatMain ?? 0xc8a44a);
    // Thin grey hair under it rather than a bald dome.
    fill(px, head.top, pal.hair);
  } else if (def.proportions.hat !== undefined) {
    paintHat(px, layout.hat!, pal, def.proportions.hat);
    // Hair shows below the hat brim rather than a shaved crown.
    fill(px, { ...head.top, h: head.top.h }, pal.hair);
  } else if (attire === "peasant") {
    // An ordinary crop. A villager is not a cossack and must not wear their
    // haircut - the oseledets is the one thing that would say otherwise.
    fill(px, head.top, pal.hair);
    band(px, head.front, 0, 0.16, pal.hair);
  } else {
    paintOseledets(px, head, pal);
  }

  // --- torso --------------------------------------------------------------
  const torso = layout.torso!;
  for (const face of [torso.front, torso.back, torso.left, torso.right]) {
    fill(px, face, pal.cloth);
  }
  fill(px, torso.top, pal.cloth);
  fill(px, torso.bottom, pal.trousers);
  fill(px, torso.back, pal.clothShade);

  for (const face of [torso.front, torso.back, torso.left, torso.right]) {
    if (attire === "skeleton") {
      /*
       * Ribs, and a red tabard hanging over them.
       *
       * The bone is on top and the cloth below, which is how the reference
       * has it: the ribcage shows at the chest and the tabard covers the
       * belly and hangs past the waist. Ribs are *pairs of lines with a gap
       * down the middle* - a solid patch of bone is a white shirt.
       */
      /*
       * The cage first, over the whole torso, and the cloth hung on after.
       *
       * It used to be bone-coloured down to the chest with ribs *drawn on
       * top in shade* - which is a white shirt with stripes. What a ribcage
       * is, is dark with bone in front of it: paint the dark, then the ribs,
       * and the gaps between them are the thing you actually see.
       */
      paintRibs(px, face, pal.skin, pal.skinShade);
      // The tabard: hung from the shoulders, open at the chest, torn off
      // short at the hem so the hip bones show under it.
      if (face === torso.back) {
        // A cloak down the back, which is what the drawing leads with.
        band(px, face, 0.08, 0.94, pal.cloth);
        band(px, face, 0.08, 0.12, pal.trim);
      } else if (face === torso.front) {
        band(px, face, 0.40, 0.44, pal.trim);
        band(px, face, 0.44, 0.74, pal.cloth);
        band(px, face, 0.70, 0.76, pal.belt);
        // Torn: the hem is not a straight line on a dead man's clothes.
        for (let x = 0; x < face.w; x++) {
          if (x % 3 === 0) put(px, face.x + x, face.y + Math.round(face.h * 0.76), pal.cloth);
        }
      } else {
        band(px, face, 0.12, 0.74, pal.cloth);
      }
      continue;
    }
    if (attire === "chakhlyk") {
      /*
       * A bare ribcage with a cloak of black feathers off the shoulders.
       *
       * The cloak hangs at the sides rather than covering the chest - in the
       * drawing you see his whole front and the black is behind his arms. So
       * the front is skin and ribs, the back is cloak, and the two side faces
       * are cloak as well, which is what makes the black read as something
       * hanging off him rather than as a shirt.
       */
      if (face === torso.front) {
        fill(px, face, pal.skin);
        for (let row = 0; row < 5; row++) {
          const y = face.y + 2 + row * 2;
          for (let x = 1; x < face.w - 1; x++) {
            if (Math.abs(x - (face.w - 1) / 2) < 0.6) continue;
            put(px, face.x + x, y, pal.skinShade);
          }
        }
        // The belt with the skull on it, low on a starved belly.
        band(px, face, 0.8, 0.88, pal.belt);
        const mid = face.x + Math.floor(face.w / 2) - 1;
        fill(px, { x: mid, y: face.y + Math.round(face.h * 0.8), w: 2, h: 2 },
          pal.skin);
        continue;
      }
      fill(px, face, pal.cloth);
      // Feathers: a ragged lower edge rather than a hem.
      for (let x = 0; x < face.w; x++) {
        const ragged = [2, 4, 1, 3, 2, 5, 1, 3, 4, 2][x % 10] ?? 2;
        for (let d = 0; d < ragged; d++) {
          put(px, face.x + x, face.y + face.h - 1 - d, pal.clothShade);
        }
      }
      continue;
    }
    if (attire === "wolf") {
      /*
       * No clothes at all.
       *
       * A вовкулака wears nothing, so the torso is the same fur as the head
       * with a paler chest and a darker line down the spine - which is what
       * makes a solid block of brown read as an animal's body rather than as
       * a brown shirt.
       */
      /*
       * Fur, and no blaze.
       *
       * There were three goes at a pale chest here - full width, an inset
       * rectangle, then a narrow strip - and all three read as something
       * sewn onto a coat. The sheet settles it: there is no blaze. He is one
       * colour from the jaw to the feet, and what makes the block read as an
       * animal is the mottling, not a marking.
       */
      paintFur(px, face, pal.skinShade, pal.skin, pal.trim);
      continue;
    }
    if (attire === "mavka") {
      /*
       * Not a shift: a fall of leaves, and a woven cord holding it on.
       *
       * The whole face is leaves rather than a band of them, because in the
       * prototype the dress *is* the willow - it hangs off her shoulders and
       * goes on down past the knee. The cord at the waist is the only thing
       * on her that a person made.
       */
      leafFall(px, face, pal.clothShade, pal.cloth, pal.hatCrown ?? pal.cloth);
      leafGlow(px, face, pal.glow ?? 0xf6e98a, pal.glowSoft ?? 0xcfd47a);
      band(px, face, 0.46, 0.56, pal.belt);
      band(px, face, 0.50, 0.52, pal.trim);
      continue;
    }
    if (attire === "orc") {
      // Webbing at the shoulders and a belt at the waist. The camouflage goes
      // on after this loop, so it lands over both.
      band(px, face, 0.06, 0.16, pal.clothShade);
      band(px, face, 0.80, 0.94, pal.belt);
    } else if (attire === "peasantWoman") {
      // Sorochka to the waist, then the плахта wrapped over it. The skirt is
      // the silhouette - it is what tells her from him across a field.
      band(px, face, 0, 0.12, pal.trim);
      const y0 = Math.round(face.h * 0.55);
      stripes(px, { x: face.x, y: face.y + y0, w: face.w, h: face.h - y0 },
        pal.skirt ?? PLAKHTA, pal.skirtStripe ?? PLAKHTA_STRIPE);
      band(px, face, 0.52, 0.58, pal.belt);
    } else if (attire === "peasant") {
      // Plain linen sorochka: embroidery at the collar, a woven sash low on
      // the hips rather than the cossack's broad belt at the waist.
      band(px, face, 0, 0.12, pal.trim);
      band(px, face, 0.80, 0.92, pal.belt);
    } else if (def.proportions.hat !== undefined) {
      // Zhupan: pale horizontal cording across the chest.
      band(px, face, 0.22, 0.30, pal.trim);
      band(px, face, 0.38, 0.46, pal.trim);
      band(px, face, 0.54, 0.62, pal.trim);
      band(px, face, 0.76, 1, pal.belt);
    } else {
      // Sorochka: embroidered collar and hem.
      band(px, face, 0, 0.12, pal.trim);
      // The wide woven belt every cossack wears.
      band(px, face, 0.76, 1, pal.belt);
    }
  }
  if (attire === "orc") {
    for (const face of [torso.front, torso.back, torso.left, torso.right]) {
      camo(px, face, pal.camoDark ?? 0x333a26, pal.camoLight ?? 0x6b7448);
    }
    paintMark(px, torso.front, pal.mark ?? 0xe4e0d4);
  } else if (attire === "peasantWoman") {
    // Намисто: strings of coral at the throat. Inset from the shoulders,
    // because a band the full width of the chest is a stripe, not a necklace.
    const f = torso.front;
    const inset = Math.max(1, Math.round(f.w * 0.22));
    const beads = { x: f.x + inset, w: f.w - 2 * inset };
    fill(px, { ...beads, y: f.y + Math.round(f.h * 0.14), h: 1 }, pal.beads ?? CORAL);
    fill(px, { ...beads, y: f.y + Math.round(f.h * 0.22), h: 1 }, pal.beads ?? CORAL);
  } else if (attire === "mavka") {
    // The lacing down the front, under the collar of rays - and then the
    // rays themselves, over everything, because light lies on top.
    const f = torso.front;
    const cx = f.x + Math.floor(f.w / 2);
    fill(px, { x: cx, y: f.y + Math.round(f.h * 0.16), w: 1,
      h: Math.round(f.h * 0.3) }, pal.belt);
    /*
     * The collar, turned down.
     *
     * In full glow across nine pixels it is a yellow bib - the brightest
     * thing on her by a distance, and the first thing the eye goes to. The
     * drawing has a faint V of *lighter leaves* at the throat - green, not
     * yellow - and saves the light itself for the brow and the wrists. Two
     * leaf tones a shade apart do that, and nothing warm belongs here.
     */
    paintRayCollar(px, f, pal.hatCrown ?? pal.cloth, pal.cloth);
    // A fainter one at the back, so she is lit from behind as well as in
    // front rather than being a painted board seen from the wrong side.
    // And the same on the back, in leaf tones: a row of pale teeth across
    // her shoulders was the brightest thing on her from behind, and the
    // drawing has nothing there at all.
    paintRayCollar(px, torso.back, pal.hatCrown ?? pal.cloth, pal.clothShade);
  } else if (attire === "wolf") {
    // Nothing down the front: fur has no placket.
  } else if (def.proportions.hat === undefined) {
    // A vertical placket of embroidery down the front of the shirt.
    const f = torso.front;
    const cx = f.x + Math.floor(f.w / 2);
    fill(px, { x: cx, y: f.y, w: 1, h: Math.round(f.h * 0.7) }, pal.trim);
    if (attire === "peasant") {
      // Tassels hanging off the sash, which is what a тканий пояс actually
      // looks like from the front.
      const ty = f.y + Math.round(f.h * 0.92);
      for (let i = 1; i < f.w; i += 3) put(px, f.x + i, ty, pal.belt);
    }
  }

  // --- arms ---------------------------------------------------------------
  for (const name of ["armR", "armL"] as const) {
    const arm = layout[name]!;
    for (const face of [arm.front, arm.back, arm.left, arm.right, arm.top]) {
      // A wolf's arms are the same fur as its body, so they need their own
      // tone or the whole silhouette is one brown block with a head on it.
      // The мавка's are bare - she wears leaves
      // over her shoulders, not sleeves, and in the prototype the arms are
      // the palest thing on her.
      fill(px, face,
        attire === "wolf" ? pal.hair
          // Bare arms: hers, a skeleton's bones, and his starved ones.
          : attire === "mavka" || attire === "skeleton" || attire === "chakhlyk"
            ? pal.skin
          : pal.cloth);
    }
    for (const face of [arm.front, arm.back, arm.left, arm.right]) {
      if (attire === "peasantWoman") {
        // The flowered sleeve of the photograph. Kept to the top third of the
        // arm: run it further and it lines up with the bands on the torso,
        // and the two together read as a striped jumper rather than as
        // embroidery on a white shirt.
        band(px, face, 0.08, 0.14, pal.trim);
        band(px, face, 0.18, 0.30, pal.trim);
        band(px, face, 0.22, 0.26, 0x2c211c);
      }
      if (attire === "orc") {
        camo(px, face, pal.camoDark ?? 0x333a26, pal.camoLight ?? 0x6b7448);
      }
      if (attire === "wolf") {
        /*
         * Fur down the whole arm, and the claws at the end of it.
         *
         * A black band for a paw was what this was, which at a distance is a
         * glove. The claws are the second thing you see of him after the
         * eyes - four of them, bone against all that black - and they are
         * what say the arm ends in a hand that opens you up.
         */
        paintFur(px, face, pal.skinShade, pal.skin, pal.trim);
        band(px, face, 0.86, 1, pal.boot);
        // The front of the paw only: claws all the way round a limb are a
        // bracelet, not a hand.
        if (face === arm.front) {
          paintClaws(px, face, pal.glow ?? 0xd6d8c2,
            pal.glowSoft ?? 0xa8aa96, pal.boot);
        }
        continue;
      }
      if (attire === "skeleton") {
        /*
         * A bone, not a sleeve.
         *
         * `paintBone` carves it out of the box: dark down both edges so what
         * is lit is a shaft a pixel or two wide, with a knob at each joint.
         * Bands of colour across the whole face - which is what this was -
         * give an arm the thickness of the box, and a skeleton with arms
         * that thick is a person in white.
         */
        paintBone(px, face, pal.skin, pal.skinShade);
        // A pauldron of cloth where it meets the shoulder, and iron at the
        // wrist so the sword has a hand to be in.
        band(px, face, 0, 0.16, pal.cloth);
        band(px, face, 0.84, 1, pal.hatMain ?? 0x9aa0a8);
        continue;
      }
      if (attire === "chakhlyk") {
        // Bare to the shoulder, with the feather cloak just covering it, and
        // the spiked bracelet off the drawing at the wrist.
        band(px, face, 0, 0.16, pal.cloth);
        band(px, face, 0.78, 0.86, pal.belt);
        band(px, face, 0.86, 0.9, pal.trim);
        continue;
      }
      if (attire === "mavka") {
        /*
         * A band of light at each wrist, and a few leaves at the shoulder.
         *
         * The wrists are the third place she is lit, after the brow and the
         * collar, and they are the ones you see while she is coming at you -
         * the arms are what move. The leaves at the top are the cloak
         * hanging off her, not a sleeve: four rows and no more, or she is
         * wearing a shirt again.
         */
        /*
         * Leaves the whole length, with the light at the wrist over them.
         *
         * Four rows and bare arm below was the old version, on the theory
         * that a cloak is not a sleeve. The drawing disagrees: her arms are
         * made of the same hanging stuff as the rest of her, and the band at
         * the wrist is light lying on leaves, not a bracelet on a wrist.
         */
        leafFall(px, face, pal.clothShade, pal.cloth,
          pal.hatCrown ?? pal.cloth);
        leafGlow(px, face, pal.glow ?? 0xf6e98a, pal.glowSoft ?? 0xcfd47a);
        // Narrow: it was a seventh of the arm in full glow and read as a
        // cuff. On the sheet it is a line of light lying across the leaves.
        band(px, face, 0.78, 0.82, pal.glowSoft ?? 0xcfd47a);
        band(px, face, 0.82, 0.86, pal.glow ?? 0xf6e98a);
        continue;
      }
      // Bare hands at the cuff.
      band(px, face, 0.78, 1, pal.skin);
      band(px, face, 0.72, 0.78, pal.trim);
    }
    fill(px, arm.bottom, pal.skin);
  }

  // --- legs ---------------------------------------------------------------
  for (const name of ["legR", "legL"] as const) {
    const leg = layout[name]!;
    for (const face of [leg.front, leg.back, leg.left, leg.right, leg.top]) {
      fill(px, face, pal.trousers);
    }
    for (const face of [leg.front, leg.back, leg.left, leg.right]) {
      if (attire === "peasantWoman") {
        // The плахта runs on down over the legs, so the two leg boxes read as
        // one skirt. Boots below it, and nothing bare in between.
        stripes(px, { x: face.x, y: face.y, w: face.w, h: Math.round(face.h * 0.62) },
          pal.skirt ?? PLAKHTA, pal.skirtStripe ?? PLAKHTA_STRIPE);
        band(px, face, 0.62, 1, pal.boot);
        continue;
      }
      if (attire === "orc") {
        camo(px, face, pal.camoDark ?? 0x333a26, pal.camoLight ?? 0x6b7448);
        // Boots to the knee, which is most of the leg on something this squat.
        band(px, face, 0.45, 1, pal.boot);
        continue;
      }
      if (attire === "wolf") {
        // The same, and for the same reason: he stands on claws too.
        paintFur(px, face, pal.skinShade, pal.skin, pal.trim);
        band(px, face, 0.88, 1, pal.boot);
        if (face === leg.front) {
          paintClaws(px, face, pal.glow ?? 0xd6d8c2,
            pal.glowSoft ?? 0xa8aa96, pal.boot);
        }
        continue;
      }
      if (attire === "chakhlyk") {
        /*
         * Black shorts, bare shins, and the long pointed boots.
         *
         * All-black legs made him a column from the waist down; what the
         * drawing has is a lot of thin bare leg between the shorts and the
         * boots, and that gap is most of what makes him look starved.
         */
        band(px, face, 0, 0.34, pal.trousers);
        band(px, face, 0.34, 0.72, pal.skin);
        band(px, face, 0.72, 1, pal.boot);
        continue;
      }
      if (attire === "skeleton") {
        /*
         * Leg bone all the way down, with a rag over the thigh.
         *
         * Trousers to the knee was the old version and it is the same
         * mistake as the arms: a solid leg is a leg with flesh on it. The
         * cloth is a short rag at the top - what is left of it - and the
         * shin is bone with dark either side.
         */
        paintBone(px, face, pal.skin, pal.skinShade);
        band(px, face, 0, 0.28, pal.trousers);
        band(px, face, 0.92, 1, pal.boot);
        continue;
      }
      if (attire === "mavka") {
        /*
         * The leaves go on down, and she is barefoot under them.
         *
         * The two leg boxes have to read as one hanging dress the way the
         * плахта does, or the willow stops at the waist and she is wearing a
         * green jumper. Ragged at the hem - the last row of leaves is dark,
         * so it ends in points rather than in a line.
         */
        /*
         * All the way to the ground, and no bare feet under it.
         *
         * The last quarter was painted her skin colour - "barefoot under the
         * leaves" - and what it actually did was put a pale cuff round the
         * bottom of each leg, which from any distance is a boot. It ended
         * the dress at the ankle and the willow with it. The drawing has the
         * leaves reaching the floor and nothing showing below them.
         */
        leafFall(px, face, pal.clothShade, pal.cloth,
          pal.hatCrown ?? pal.cloth);
        // The hem, torn into points rather than cut in a line.
        band(px, face, 0.96, 1, pal.clothShade);
        leafGlow(px, face, pal.glow ?? 0xf6e98a, pal.glowSoft ?? 0xcfd47a);
        continue;
      }
      // Sharovary are baggy and tuck into the boot at mid-calf.
      band(px, face, 0.55, 1, pal.boot);
      band(px, face, 0.52, 0.58, 0x171310);
    }
    fill(px, leg.bottom, attire === "mavka" ? pal.skinShade : 0x171310);
  }

  /* --- the second layer, where a body has one ----------------------------- */
  if (attire === "mavka") {
    /*
     * Hair, a robe, sleeves and a skirt - as things, not as paint.
     *
     * Everything here is *outside* the body, so what is not painted is a
     * hole: the material throws those pixels away. That is the whole point
     * of the layer. Hair can end in strands, a sleeve in rags and a skirt in
     * points, none of which a box painted on a limb can do - a painted hem
     * is always the bottom edge of the box it is painted on.
     */
    const dark = pal.clothShade;
    const mid = pal.cloth;
    const light = pal.hatCrown ?? pal.cloth;

    /**
     * Leaves that thin out towards the bottom, leaving holes.
     *
     * `from` is the fraction of the height where the hem starts breaking up.
     * Below it a column survives only if its own hash says so, and the
     * further down the fewer do - which is a ragged edge rather than a line.
     */
    const ragged = (r: Rect, from: number): void => {
      leafFall(px, r, dark, mid, light);
      leafGlow(px, r, pal.glow ?? 0xf6e98a, pal.glowSoft ?? 0xcfd47a);
      const start = Math.round(r.h * from);
      for (let y = start; y < r.h; y++) {
        const through = (y - start + 1) / Math.max(1, r.h - start);
        for (let x = 0; x < r.w; x++) {
          if (hashPixel(r.x + x * 3, r.y + y * 7) < through) {
            clear(px, r.x + x, r.y + y);
          }
        }
      }
    };

    const hair = layout.headOver;
    if (hair !== undefined) {
      for (const face of [hair.back, hair.left, hair.right, hair.top]) {
        ragged(face, 0.62);
      }
      /*
       * The front of the hair is nearly all hole.
       *
       * It has to be: behind it is her face, and hair painted across the
       * front of the head is a hood. What is left is the fringe over the
       * brow and a strand down each side, which is what the drawing has.
       */
      const f = hair.front;
      // Two courses of fringe and one strand each side. Three and two took
      // half the face with them and she was looking out of a hood.
      leafFall(px, { ...f, h: 2 }, dark, mid, light);
      for (const ex of [f.x, f.x + f.w - 1]) {
        leafFall(px, { x: ex, y: f.y, w: 1, h: f.h }, dark, mid, light);
      }
      // And the fringe itself broken up, so it is hair and not a helmet.
      for (let x = 0; x < f.w; x++) {
        if (hashPixel(f.x + x, f.y + 91) > 0.55) clear(px, f.x + x, f.y + 2);
      }
      fill(px, hair.bottom, dark);
    }

    const robe = layout.torsoOver;
    if (robe !== undefined) {
      for (const face of [robe.front, robe.back, robe.left, robe.right]) {
        ragged(face, 0.72);
      }
      // Open at the throat, which is where her own skin shows.
      const f = robe.front;
      const mid2 = f.x + Math.floor(f.w / 2);
      for (let y = 0; y < Math.round(f.h * 0.22); y++) {
        const wide = Math.max(1, Math.round(f.w * 0.24) - y);
        for (let x = mid2 - wide; x <= mid2 + wide; x++) clear(px, x, f.y + y);
      }
      fill(px, robe.top, dark);
      fill(px, robe.bottom, dark);
    }

    const sleeve = layout.armROver;
    const sleeveL = layout.armLOver;
    for (const arm of [sleeve, sleeveL]) {
      if (arm === undefined) continue;
      for (const face of [arm.front, arm.back, arm.left, arm.right]) {
        // Rags from halfway down: the drawing's sleeves hang past the wrist
        // in strips.
        ragged(face, 0.5);
      }
      fill(px, arm.top, dark);
      fill(px, arm.bottom, dark);
    }

    for (const name of ["legROver", "legLOver"] as const) {
      const skirt = layout[name];
      if (skirt === undefined) continue;
      for (const face of [skirt.front, skirt.back, skirt.left, skirt.right]) {
        ragged(face, 0.55);
      }
      fill(px, skirt.top, dark);
      fill(px, skirt.bottom, dark);
    }
  }

  return px;
}

// --- front view ------------------------------------------------------------

export interface PixelImage { w: number; h: number; px: Uint8ClampedArray; }

/**
 * Assemble the model's front faces into a flat paper-doll view.
 *
 * Used by the in-game skin picker and by scripts/export-skins.ts, so what the
 * player sees when choosing is generated from exactly the same pixels the 3D
 * model is textured with.
 */
export function paintFrontView(def: SkinDef): PixelImage {
  const skin = paintSkin(def);
  const layout = layoutSkin(def.proportions);
  const p = def.proportions;

  const hatH = p.hat?.h ?? 0;
  const bodyW = p.torso.w + 2 * p.arm.w;
  const w = Math.max(bodyW, p.hat?.w ?? 0, p.head.w) + 2;
  const h = hatH + p.head.h + p.torso.h + p.leg.h + 2;
  const out = new Uint8ClampedArray(w * h * 4);

  const blit = (r: Rect, dx: number, dy: number): void => {
    for (let yy = 0; yy < r.h; yy++) {
      for (let xx = 0; xx < r.w; xx++) {
        const si = ((r.y + yy) * SKIN_TEX + (r.x + xx)) * 4;
        if (skin[si + 3] === 0) continue;
        const tx = dx + xx;
        const ty = dy + yy;
        if (tx < 0 || ty < 0 || tx >= w || ty >= h) continue;
        const di = (ty * w + tx) * 4;
        out[di] = skin[si]!;
        out[di + 1] = skin[si + 1]!;
        out[di + 2] = skin[si + 2]!;
        out[di + 3] = 255;
      }
    }
  };

  const cx = Math.floor(w / 2);
  let y = 1;

  if (p.hat !== undefined) {
    blit(layout.hat!.front, cx - Math.floor(p.hat.w / 2), y);
    y += p.hat.h;
  }
  blit(layout.head!.front, cx - Math.floor(p.head.w / 2), y);
  y += p.head.h;

  const torsoX = cx - Math.floor(p.torso.w / 2);
  blit(layout.torso!.front, torsoX, y);
  blit(layout.armR!.front, torsoX - p.arm.w, y);
  blit(layout.armL!.front, torsoX + p.torso.w, y);
  y += p.torso.h;

  blit(layout.legR!.front, cx - p.leg.w, y);
  blit(layout.legL!.front, cx, y);

  return { w, h, px: out };
}
